import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { Box, Paper, Typography, Alert, useTheme, FormControl, Select, MenuItem, Chip, Button, Table, TableBody, TableCell, TableHead, TableRow, Divider, CircularProgress, Collapse, IconButton, ToggleButtonGroup, ToggleButton, Tooltip, useMediaQuery, Link } from '@mui/material';
import { useDisabledValueChainNames } from '../shared/hooks/useDisabledValueChainNames';
import { useValueChainMetaInfo } from '../shared/hooks/useValueChainMetaInfo';
import { useAuth } from '../../components/auth/AuthProvider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import hsCodeValueChainService from '../hsCodeValueChain/hsCodeValueChainService';
import SankeyDiagram from '../shared/components/SankeyDiagram';
import FeatureNavTabs from '../shared/components/FeatureNavTabs';
import { api } from '../../api/setup';
import ReactECharts from 'echarts-for-react';
import { OpenAPI } from '../../api/core/OpenAPI';

const SHOULD_DEBUG = process.env.NODE_ENV !== 'production' && process.env.REACT_APP_ENABLE_DEBUG_LOGGING === 'true';

// Types
interface GraphNode {
  element_id: string;
  name: string;
  labels?: string[];
  properties?: Record<string, any>;
  n_iteration?: number;
  hs_code?: string[];
  hs_code_description?: string[];
  function?: string;
  members?: GraphNode[];
  source_db?: string;
}

interface SankeyData {
  nodes: Array<{
    name: any;
    value?: number;
    itemStyle?: { color: string };
    depth?: number;
    label?: any;
    nodeData: {
      type: any;
      properties: any;
      elementId: any;
      n_iteration: any;
    };
  }>;
  links: any[];
}

interface Country {
  code: string;
  name: string;
}

interface TradeYear {
  year: number;
}

interface HSCodeDescriptionsByCode {
  [code: string]: {
    description_2?: string | null;
    description_4?: string | null;
    description_6?: string | null;
  };
}

interface WorldSummary {
  exports_to_world: any[];
  imports_from_world: any[];
}

interface RCAData {
  [key: string]: number;
}

interface ExpandedRows {
  [key: string]: boolean;
}

interface TechExpanded {
  [key: string]: boolean;
}

interface TechCardExpanded {
  [key: string]: boolean;
}

const HSGroupedHSCodeAnalysis: React.FC = () => {
  const theme = useTheme();
  const { isAuthenticated } = useAuth();
  const [selectedElementId, setSelectedElementId] = useState<string>('');
  const [availableRoots, setAvailableRoots] = useState<GraphNode[]>([]);
  const [graphData, setGraphData] = useState<SankeyData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [maxIterations, setMaxIterations] = useState<number>(isMobile ? 2 : 5);
  const [minFlowThreshold, setMinFlowThreshold] = useState<number>(0.001);
  const [countries, setCountries] = useState<Country[]>([]); // Keep for API loading, but use accessibleCountries for display
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [selectedGroup, setSelectedGroup] = useState<any>(null);
  const [years, setYears] = useState<TradeYear[]>([]);
  const [selectedYear, setSelectedYear] = useState<string>('');
  const [selectedMembers, setSelectedMembers] = useState<GraphNode[]>([]);
  const [clearFocusTick, setClearFocusTick] = useState<number>(0);
  // Use a stable reference for empty world summary to prevent unnecessary re-renders
  const emptyWorldSummary = useMemo(() => ({ exports_to_world: [], imports_from_world: [] }), []);
  const [worldSummary, setWorldSummary] = useState<WorldSummary>(emptyWorldSummary);
  const [worldLoading, setWorldLoading] = useState<boolean>(false);
  const [expandedRows, setExpandedRows] = useState<ExpandedRows>({});
  const [visibleCount, setVisibleCount] = useState<number>(10);
  const [techExpanded, setTechExpanded] = useState<TechExpanded>({});
  const [techCardExpanded, setTechCardExpanded] = useState<TechCardExpanded>({});
  const [sankeyHSCodes, setSankeyHSCodes] = useState<string[]>([]);
  const [rcaByCode, setRcaByCode] = useState<RCAData>({});
  const [colorMode, setColorMode] = useState<'balance' | 'rca' | 'exports' | 'imports'>('balance');
  const [rcaUpper, setRcaUpper] = useState<number>(10);
  const [treemapFlow, setTreemapFlow] = useState<'exports' | 'imports'>('exports');
  const [treemapLevel, setTreemapLevel] = useState<6 | 4 | 2 | 'section'>(6);
  const [treemapExporting, setTreemapExporting] = useState<boolean>(false);
  const [treemapExportError, setTreemapExportError] = useState<string | null>(null);
  const [hsDescriptionsByCode, setHsDescriptionsByCode] = useState<HSCodeDescriptionsByCode>({});
  const [treemapInfoExpanded, setTreemapInfoExpanded] = useState<boolean>(false);
  const [treemapSelection, setTreemapSelection] = useState<null | {
    level: 6 | 4 | 2 | 'section';
    flow: 'exports' | 'imports';
    code: string;
    exportsTotal: number;
    importsTotal: number;
    hs6Items: Array<{ code: string; description: string; value: number; exportsValue: number; importsValue: number }>;
  }>(null);

  const exportTreemapXlsx = async () => {
    if (!selectedCountry || !selectedYear) {
      setTreemapExportError('Select a country and a year to export.');
      return;
    }
    if (!Array.isArray(sankeyHSCodes) || sankeyHSCodes.length === 0) {
      setTreemapExportError('No HS codes available to export for the current graph.');
      return;
    }
    const hs6 = Array.from(new Set(sankeyHSCodes.map(normalizeHS).filter(Boolean)));
    if (hs6.length === 0) {
      setTreemapExportError('No valid HS6 codes available to export.');
      return;
    }

    setTreemapExporting(true);
    setTreemapExportError(null);
    try {
      const token = localStorage.getItem('auth_access_token');
      const res = await fetch(`${OpenAPI.BASE}/api/v1/trade/treemap-export-xlsx`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          value_chain: selectedRootName || (availableRoots || []).find((r: any) => String(r.element_id) === String(selectedElementId))?.name || 'Selected value chain',
          country: String(selectedCountry), // backward compatible
          country_code: Number(selectedCountry),
          country_name: selectedCountryName,
          year: Number(selectedYear),
          hs_codes: hs6,
          root_element_id: selectedRootElementId || selectedElementId || null,
          max_iterations: Number(maxIterations),
          min_threshold: Number(minFlowThreshold),
        }),
      });
      if (!res.ok) {
        let msg = `Export failed (${res.status})`;
        try {
          const j = await res.json();
          msg = j?.detail || j?.message || msg;
        } catch {}
        throw new Error(msg);
      }
      const blob = await res.blob();
      const cd = res.headers.get('content-disposition') || '';
      const m = cd.match(/filename="([^"]+)"/i);
      const filename = m?.[1] || `trade_treemap_${selectedCountryName}_${selectedYear}.xlsx`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      setTreemapExportError(e?.message || 'Failed to export XLSX');
    } finally {
      setTreemapExporting(false);
    }
  };
  // Persist the initially selected root for stable dropdown label and cross-view persistence
  const [selectedRootElementId, setSelectedRootElementId] = useState<string>('');
  const [selectedRootName, setSelectedRootName] = useState<string>('');
  const [selectedRootSourceDb, setSelectedRootSourceDb] = useState<string>('');
  const STORAGE_ID_KEY = 'vc.selectedRootId';
  const STORAGE_NAME_KEY = 'vc.selectedRootName';
  const STORAGE_SOURCE_DB_KEY = 'vc.selectedRootSourceDb';

  // Fetch value chain meta info (info and shortcomings) for the selected root using source_db
  const { info: valueChainInfo, shortcomings: valueChainShortcomings } = useValueChainMetaInfo(selectedRootSourceDb);

  // Optional list of names to show as disabled in the value chain dropdown
  const disabledValueChainNames = useDisabledValueChainNames();

  // Unified handler so both Select.onChange and MenuItem.onClick update reliably
  const handleRootChange = (eventOrValue) => {
    const newId = typeof eventOrValue === 'string' ? eventOrValue : eventOrValue?.target?.value;
    if (newId == null) return;
    setSelectedElementId(String(newId));
    const n = (availableRoots || []).find((x) => String(x.element_id) === String(newId));
    const name = n ? n.name : `Element ${newId}`;
    const sourceDb = n?.source_db || '';
    setSelectedRootElementId(String(newId));
    setSelectedRootName(String(name));
    setSelectedRootSourceDb(String(sourceDb));
    try {
      localStorage.setItem(STORAGE_ID_KEY, String(newId));
      localStorage.setItem(STORAGE_NAME_KEY, String(name));
      localStorage.setItem(STORAGE_SOURCE_DB_KEY, String(sourceDb));
    } catch {}
  };

  const normalizeHS = (v) => {
    if (v == null) return '';
    const s = String(v);
    const m = s.match(/(\d{6})/);
    if (m) return m[1];
    // If numeric with fewer than 6 digits, left-pad
    const digits = s.replace(/\D/g, '');
    if (digits.length > 0) return digits.padStart(6, '0').slice(-6);
    return s;
  };

  const normalizeSectionCode = (v: string) => {
    const t = (v || '').replace(/\s+/g, ' ').trim();
    if (!t) return '';
    // Stored section values may already include the word "Section" (e.g., "Section 16").
    // Normalize to just the code portion so UI can safely render "Section {code}".
    const stripped = t.replace(/^section\b\s*/i, '').trim();
    return stripped || t;
  };

  const truncateText = (s: string, n: number) => {
    const t = (s || '').replace(/\s+/g, ' ').trim();
    if (!t) return '';
    return t.length > n ? t.slice(0, Math.max(0, n - 1)) + '…' : t;
  };
  
  // UI helper: if a label looks like MOSTLY ALL CAPS, render it in sentence case for a calmer look.
  const softenAllCaps = (s: string) => {
    const t = (s || '').replace(/\s+/g, ' ').trim();
    if (!t) return '';
    const letters = t.replace(/[^A-Za-z]+/g, '');
    if (letters.length < 6) return t;
    const upper = (letters.match(/[A-Z]/g) || []).length;
    const ratio = letters.length ? upper / letters.length : 0;
    if (ratio >= 0.85) {
      const lower = t.toLowerCase();
      return lower ? lower[0].toUpperCase() + lower.slice(1) : lower;
    }
    return t;
  };

  // Fetch HS2/HS4/HS6 descriptions from Postgres for the HS6 codes present in the Sankey
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        if (!Array.isArray(sankeyHSCodes) || sankeyHSCodes.length === 0) {
          if (!cancelled) setHsDescriptionsByCode({});
          return;
        }
        const hs6 = Array.from(new Set(sankeyHSCodes.map(normalizeHS).filter(Boolean)));
        if (hs6.length === 0) {
          if (!cancelled) setHsDescriptionsByCode({});
          return;
        }
        const token = localStorage.getItem('auth_access_token');
        const res = await fetch(`${OpenAPI.BASE}/api/v1/hs-codes/descriptions`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ codes: hs6 }),
        });
        if (!res.ok) throw new Error(`Failed to load HS descriptions (${res.status})`);
        const j = await res.json();
        const items = (j && typeof j === 'object' && j.items && typeof j.items === 'object') ? j.items : {};
        if (!cancelled) setHsDescriptionsByCode(items as any);
      } catch (e) {
        if (SHOULD_DEBUG) console.error('Failed to load hs descriptions for treemap', e);
        if (!cancelled) setHsDescriptionsByCode({});
      }
    };
    load();
    return () => { cancelled = true; };
  }, [sankeyHSCodes]);

  // DEVELOPMENT MODE: Use all countries from API instead of permission-filtered
  // TODO: Restore permission-based filtering when re-enabling permissions
  const accessibleCountries = useMemo(() => {
    // For public mode, return all countries from the API
    // API returns objects with country_code and country_name, map to code and name
    return countries.map((c: any) => ({ 
      code: c.country_code || c.code, 
      name: c.country_name || c.name 
    }));
  }, [countries]);

  /* ORIGINAL PERMISSION-BASED CODE - Uncomment to restore
  const accessibleCountries = useMemo(() => {
    return getCountryNamesByLevel('read');
  }, [getCountryNamesByLevel]);
  */

  const selectedCountryName = useMemo(() => {
    if (!selectedCountry) return '—';
    const match = accessibleCountries.find(c => String(c.code) === String(selectedCountry));
    return match?.name || String(selectedCountry);
  }, [accessibleCountries, selectedCountry]);
  const hsBalanceMap = useMemo(() => {
    const map = {};
    const expMap = new Map();
    const impMap = new Map();
    worldSummary.exports_to_world.forEach(r => expMap.set(r.hs_code, Number(r.total_value) || 0));
    worldSummary.imports_from_world.forEach(r => impMap.set(r.hs_code, Number(r.total_value) || 0));
    const codes = new Set([...expMap.keys(), ...impMap.keys()]);
    codes.forEach(code => {
      const ev = expMap.get(code) || 0;
      const iv = impMap.get(code) || 0;
      const denom = ev + iv;
      const norm = denom > 0 ? (ev - iv) / denom : 0;
      map[code] = norm;
    });
    return map;
  }, [worldSummary.exports_to_world, worldSummary.imports_from_world]);

  // Country-specific trade map for tooltip: { hs_code -> { exports, imports, balance } }
  const hsTradeMap = useMemo(() => {
    const map = {};
    try {
      const expMap = new Map();
      const impMap = new Map();
      worldSummary.exports_to_world.forEach(r => expMap.set(normalizeHS(r.hs_code), Number(r.total_value) || 0));
      worldSummary.imports_from_world.forEach(r => impMap.set(normalizeHS(r.hs_code), Number(r.total_value) || 0));
      const codes = new Set([...expMap.keys(), ...impMap.keys()]);
      codes.forEach(code => {
        const ev = expMap.get(code) || 0;
        const iv = impMap.get(code) || 0;
        map[code] = { exports: ev, imports: iv, balance: ev - iv };
      });
    } catch {}
    return map;
  }, [worldSummary.exports_to_world, worldSummary.imports_from_world, normalizeHS]);

  // Caps for log-scaled exports/imports coloring (computed from HS codes present in the current Sankey)
  const tradeColorCaps = useMemo(() => {
    const hs6 = Array.isArray(sankeyHSCodes) ? Array.from(new Set(sankeyHSCodes.map(normalizeHS).filter(Boolean))) : [];
    let exportsCap = 0;
    let importsCap = 0;
    hs6.forEach((code) => {
      const row: any = (hsTradeMap as any)?.[code];
      const ev = Number(row?.exports) || 0;
      const iv = Number(row?.imports) || 0;
      if (Number.isFinite(ev)) exportsCap = Math.max(exportsCap, ev);
      if (Number.isFinite(iv)) importsCap = Math.max(importsCap, iv);
    });
    return { exportsCap, importsCap };
  }, [sankeyHSCodes, hsTradeMap, normalizeHS]);

  // Compute path weight (outflow) per HS6 code from the current Sankey graph.
  // Important: ignore iteration number; aggregate purely by HS6 across all layers.
  const hsPathWeightMap = useMemo(() => {
    const map: Record<string, number> = {};
    if (!graphData || !Array.isArray(graphData.nodes) || !Array.isArray(graphData.links)) return map;

    // Match EXACT 6-digit sequences (avoid accidentally spanning iteration digits).
    const hs6Re = /(^|\D)(\d{6})(?!\d)/g;
    const normalizeHS6 = (v: any) => {
      if (v == null) return '';
      const digits = String(v).replace(/\D/g, '');
      if (!digits) return '';
      return digits.padStart(6, '0').slice(-6);
    };
    const extractHS6 = (v: any): string[] => {
      const out: string[] = [];
      if (v == null) return out;
      const pushFromString = (s: string) => {
        if (!s) return;
        hs6Re.lastIndex = 0;
        let m: RegExpExecArray | null;
        while ((m = hs6Re.exec(s)) !== null) {
          const code = normalizeHS6(m[2]);
          if (code && code.length === 6) out.push(code);
        }
      };
      if (Array.isArray(v)) {
        v.forEach((it) => {
          if (typeof it === 'string' || typeof it === 'number') pushFromString(String(it));
        });
      } else if (typeof v === 'string' || typeof v === 'number') {
        pushFromString(String(v));
      }
      return Array.from(new Set(out));
    };

    // Map node name -> HS6 codes (plural to be robust; usually 1).
    const nameToCodes = new Map<string, string[]>();
    (graphData.nodes || []).forEach((n: any) => {
      const p = n?.nodeData?.properties || {};
      const codes = new Set<string>();
      // Prefer explicit hs_code list, then fallback to names.
      extractHS6(p?.hs_code).forEach((c) => codes.add(c));
      extractHS6(p?.name).forEach((c) => codes.add(c));
      extractHS6(n?.name).forEach((c) => codes.add(c));
      if (codes.size > 0 && typeof n?.name === 'string') nameToCodes.set(n.name, Array.from(codes));
    });

    // Sum outgoing flow values per HS6 code (ignore iteration; aggregate across all layers).
    (graphData.links || []).forEach((l: any) => {
      const v = Number(l?.value) || 0;
      if (!Number.isFinite(v) || v <= 0) return;
      const codes = nameToCodes.get(l.source);
      if (!codes || codes.length === 0) return;
      if (codes.length === 1) {
        const c = codes[0];
        map[c] = (map[c] || 0) + v;
      } else {
        // If a Sankey node happens to carry multiple HS6 codes, distribute evenly.
        const share = v / codes.length;
        codes.forEach((c) => {
          map[c] = (map[c] || 0) + share;
        });
      }
    });

    return map;
  }, [graphData]);

  // HS6 value share (for tables): normalize HS6 weights across ALL HS6 codes present in the Sankey.
  // Shown as percent, iteration-agnostic.
  const hs6ValueShare = useMemo(() => {
    const normalizeHS6 = (v: any) => {
      if (v == null) return '';
      const digits = String(v).replace(/\D/g, '');
      if (!digits) return '';
      return digits.padStart(6, '0').slice(-6);
    };

    const hs6List = Array.isArray(sankeyHSCodes) ? sankeyHSCodes.map(normalizeHS6).filter(Boolean) : [];
    const unique = Array.from(new Set(hs6List));

    let total = 0;
    unique.forEach((code6) => {
      const w = Number((hsPathWeightMap as any)?.[code6] || 0);
      if (Number.isFinite(w) && w > 0) total += w;
    });

    const map: Record<string, number> = {};
    unique.forEach((code6) => {
      const w = Number((hsPathWeightMap as any)?.[code6] || 0);
      const share = total > 0 && Number.isFinite(w) && w > 0 ? (w / total) : 0;
      map[code6] = share;
    });

    return { total, map };
  }, [sankeyHSCodes, hsPathWeightMap]);

  // Map HS code to description (best effort from node properties)
  const hsDescriptionMap = useMemo(() => {
    const map = {};
    if (!graphData || !graphData.nodes) return map;
    const sixDigit = /\b(\d{6})\b/;
    (graphData.nodes || []).forEach(n => {
      const p = n?.nodeData?.properties || {};
      let code = null;
      const hs = p.hs_code;
      if (Array.isArray(hs)) {
        for (const v of hs) { if (typeof v === 'string') { const m = v.match(sixDigit); if (m) { code = m[1]; break; } } }
      } else if (typeof hs === 'string') {
        const m = hs.match(sixDigit); if (m) code = m[1];
      }
      if (!code && typeof p.name === 'string') { const m = p.name.match(sixDigit); if (m) code = m[1]; }
      if (!code && typeof n.name === 'string') { const m = n.name.match(sixDigit); if (m) code = m[1]; }
      if (code && !map[code]) map[code] = p.hs_code_description || p.name || '';
    });
    return map;
  }, [graphData]);

  // Map HS code -> array of grouped TechnologyTypes with function/description (from grouped node members)
  const hsTechTypesMap = useMemo(() => {
    const map = {};
    if (!graphData || !graphData.nodes) return map;
    const sixDigit = /\b(\d{6})\b/;
    (graphData.nodes || []).forEach(n => {
      const p = n?.nodeData?.properties || {};
      // Determine HS codes associated with this grouped node
      const codes = new Set();
      const hs = p.hs_code;
      if (Array.isArray(hs)) {
        hs.forEach(v => { if (typeof v === 'string') { const m = v.match(sixDigit); if (m) codes.add(m[1]); } });
      } else if (typeof hs === 'string') {
        const m = hs.match(sixDigit); if (m) codes.add(m[1]);
      }
      if (typeof p.name === 'string') { const m = p.name.match(sixDigit); if (m) codes.add(m[1]); }

      if (codes.size === 0) return;
      const members = Array.isArray(p.members) ? p.members : [];
      const techItems = members
        .filter(m => Array.isArray(m.labels) ? m.labels.includes('TechnologyType') : (m.labels?.[0] === 'TechnologyType'))
        .map(m => ({
          name: m?.properties?.name || m.id,
          funcText: m?.properties?.function || m?.properties?.hs_code_description || ''
        }));
      if (techItems.length === 0) return;
      codes.forEach((code: string) => {
        if (!map[code]) map[code] = [];
        // push unique by name
        techItems.forEach(t => { if (!map[code].some(x => x.name === t.name)) map[code].push(t); });
      });
    });
    return map;
  }, [graphData]);

  // Number formatters
  const fmt2 = useMemo(() => new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), []);
  const fmt3 = useMemo(() => new Intl.NumberFormat('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 }), []);
  const fmtPct2 = useMemo(() => new Intl.NumberFormat('en-US', { style: 'percent', minimumFractionDigits: 2, maximumFractionDigits: 2 }), []);
  const fmtCompact1 = useMemo(() => new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }), []);
  const fmtCompactUsd = useMemo(() => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1 }), []);

  const InlineInfo = useMemo(() => {
    const Comp = ({ title }: { title: React.ReactNode }) => (
      <Tooltip arrow placement="top" title={title} enterTouchDelay={50}>
        <Box
          component="span"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            color: 'text.secondary',
            cursor: 'help',
            fontSize: 14,
            lineHeight: 1,
          }}
          aria-label="Info"
        >
          <InfoOutlinedIcon fontSize="inherit" />
        </Box>
      </Tooltip>
    );
    return Comp;
  }, []);

  // Treemap base rows (HS6) with BOTH exports and imports, independent of treemap sizing choice.
  const treemapRows6 = useMemo(() => {
    const expRows = (worldSummary?.exports_to_world || []) as any[];
    const impRows = (worldSummary?.imports_from_world || []) as any[];
    const expMap = new Map<string, number>();
    const impMap = new Map<string, number>();
    expRows.forEach(r => {
      const code6 = normalizeHS(r?.hs_code);
      if (!code6) return;
      const v = Number(r?.total_value) || 0;
      if (!Number.isFinite(v) || v <= 0) return;
      expMap.set(code6, (expMap.get(code6) || 0) + v);
    });
    impRows.forEach(r => {
      const code6 = normalizeHS(r?.hs_code);
      if (!code6) return;
      const v = Number(r?.total_value) || 0;
      if (!Number.isFinite(v) || v <= 0) return;
      impMap.set(code6, (impMap.get(code6) || 0) + v);
    });
    const codes = new Set<string>([...expMap.keys(), ...impMap.keys()]);
    const out: Array<{ hs6: string; exportsValue: number; importsValue: number; description: string }> = [];
    codes.forEach(code6 => {
      const exportsValue = Number(expMap.get(code6) || 0);
      const importsValue = Number(impMap.get(code6) || 0);
      if ((exportsValue + importsValue) <= 0) return;
      const fromPg = (hsDescriptionsByCode as any)?.[code6]?.description_6;
      const rawDesc = fromPg != null ? fromPg : (hsDescriptionMap as any)[code6];
      const desc = rawDesc != null ? String(rawDesc) : '';
      out.push({ hs6: code6, exportsValue, importsValue, description: desc });
    });
    return out;
  }, [worldSummary, hsDescriptionsByCode, hsDescriptionMap]);

  const treemapData = useMemo(() => {
    const agg = new Map<string, { exportsValue: number; importsValue: number; hs6Items: Array<{ code: string; description: string; value: number; exportsValue: number; importsValue: number }> }>();
    for (const row of treemapRows6) {
      let k = '';
      if (treemapLevel === 'section') {
        const d = (hsDescriptionsByCode as any)?.[normalizeHS(row.hs6)] || {};
        const secRaw = d?.section != null ? String(d.section).trim() : '';
        const sec = normalizeSectionCode(secRaw);
        k = sec || 'Unknown';
      } else {
        const keyLen = treemapLevel;
        k = String(row.hs6).slice(0, keyLen);
      }
      if (!k) continue;
      const exportsValue = Number((row as any).exportsValue || 0);
      const importsValue = Number((row as any).importsValue || 0);
      const v = treemapFlow === 'exports' ? exportsValue : importsValue;
      const hs6 = normalizeHS(row.hs6);
      const pw = hs6 ? Number((hsPathWeightMap as any)[hs6] || 0) : 0;
      const prev = agg.get(k);
      if (!prev) {
        agg.set(k, {
          exportsValue,
          importsValue,
          hs6Items: [{ code: row.hs6, description: row.description || '', value: v, exportsValue, importsValue, pathWeight: pw } as any]
        });
      } else {
        agg.set(k, {
          exportsValue: (Number(prev.exportsValue) || 0) + exportsValue,
          importsValue: (Number(prev.importsValue) || 0) + importsValue,
          hs6Items: [...prev.hs6Items, { code: row.hs6, description: row.description || '', value: v, exportsValue, importsValue, pathWeight: pw } as any]
        });
      }
    }
    return Array.from(agg.entries())
      .map(([code, it]) => {
        const exportsValue = Number(it.exportsValue) || 0;
        const importsValue = Number(it.importsValue) || 0;
        const value = treemapFlow === 'exports' ? exportsValue : importsValue;
        // Path weight aggregated across HS6 children (iteration-agnostic).
        const hs6Items = (it.hs6Items || []) as any[];
        const seen = new Set<string>();
        let pathWeight = 0;
        hs6Items.forEach((x) => {
          const c6 = normalizeHS(x?.code);
          if (!c6 || seen.has(c6)) return;
          seen.add(c6);
          const w = Number((hsPathWeightMap as any)[c6] || 0);
          if (Number.isFinite(w) && w > 0) pathWeight += w;
        });
        return [code, { ...it, exportsValue, importsValue, value, pathWeight }] as any;
      })
      .filter(([, it]) => (Number(it?.value) || 0) > 0)
      .sort((a, b) => (Number(b?.[1]?.value) || 0) - (Number(a?.[1]?.value) || 0))
      .map(([code, it]) => {
        // Keep tooltip lists deterministic and useful
        const hs6Items = (it.hs6Items || [])
          .filter(x => x && x.code)
          .sort((a, b) => (Number(b.value) || 0) - (Number(a.value) || 0));
        return { name: code, value: it.value, exportsValue: it.exportsValue, importsValue: it.importsValue, hs6Items, pathWeight: Number(it.pathWeight) || 0 };
      });
  }, [treemapRows6, treemapLevel, hsDescriptionsByCode, treemapFlow, hsPathWeightMap]);

  // Compute ranks/denominator from the *Sankey universe* (not the trade-data subset).
  // Example: if the Sankey includes 10 HS2 codes but trade exists for only 4, ranks are computed across all 10.
  const treemapUniverseWeights = useMemo(() => {
    const hs6List = Array.isArray(sankeyHSCodes) ? sankeyHSCodes.map(normalizeHS).filter(Boolean) : [];
    const uniqueHs6 = Array.from(new Set(hs6List));

    const keyForHs6 = (hs6: string) => {
      if (!hs6) return '';
      if (treemapLevel === 6) return hs6;
      if (treemapLevel === 4) return hs6.slice(0, 4);
      if (treemapLevel === 2) return hs6.slice(0, 2);
      // section
      const d = (hsDescriptionsByCode as any)?.[normalizeHS(hs6)] || {};
      const secRaw = d?.section != null ? String(d.section).trim() : '';
      const sec = normalizeSectionCode(secRaw);
      return sec || 'Unknown';
    };

    const weightsByKey = new Map<string, { weight: number; hs6Items: string[] }>();
    uniqueHs6.forEach((hs6) => {
      const key = keyForHs6(hs6);
      if (!key) return;
      const w = Number((hsPathWeightMap as any)?.[hs6] || 0);
      const prev = weightsByKey.get(key);
      if (!prev) {
        weightsByKey.set(key, { weight: Number.isFinite(w) && w > 0 ? w : 0, hs6Items: [hs6] });
      } else {
        prev.weight += (Number.isFinite(w) && w > 0) ? w : 0;
        prev.hs6Items.push(hs6);
      }
    });

    const keys = Array.from(weightsByKey.keys());
    keys.sort((a, b) => {
      const wa = Number(weightsByKey.get(a)?.weight || 0);
      const wb = Number(weightsByKey.get(b)?.weight || 0);
      if (wb !== wa) return wb - wa;
      return String(a).localeCompare(String(b));
    });

    const rankByKey = new Map<string, number>();
    keys.forEach((k, idx) => rankByKey.set(String(k), idx + 1));

    const total = keys.reduce((s, k) => s + (Number(weightsByKey.get(k)?.weight || 0)), 0);

    return {
      keyForHs6,
      weightsByKey,
      rankByKey,
      totalWeight: total,
    };
  }, [sankeyHSCodes, treemapLevel, hsDescriptionsByCode, hsPathWeightMap]);

  // Derive rank + relative value share (per current view) from the aggregated weights,
  // but using the Sankey universe for ranks and normalization.
  // - raw weight: sum of Sankey flow weights attributed to HS6 codes (iteration-agnostic)
  // - relative value share: weight / sum(weights) across ALL Sankey codes at this HS level
  // - rank: 1 for highest weight, 2 for second highest, ...
  const treemapRankedData = useMemo(() => {
    const items = (treemapData || []).map((d: any) => ({ ...d }));
    const totalWeight = Number(treemapUniverseWeights?.totalWeight || 0);
    items.forEach((d: any) => {
      const w = Number(d?.pathWeight) || 0;
      const share = totalWeight > 0 && Number.isFinite(w) && w > 0 ? (w / totalWeight) : 0;
      d.relativeValueShare = share;
    });
    items.forEach((d: any) => {
      const key = String(d?.name ?? '');
      d.rank = treemapUniverseWeights?.rankByKey?.get(key) || null;
      d._totalPathWeight = totalWeight; // for tooltip fallbacks
    });
    return items;
  }, [treemapData, treemapUniverseWeights]);

  const treemapTotalUniverseWeight = useMemo(() => Number(treemapUniverseWeights?.totalWeight || 0), [treemapUniverseWeights]);

  // Treemap color scale: per-view log + percentile clipping, with gray for zero/unknown.
  const treemapPathWeightStats = useMemo(() => {
    const eps = 1e-6;
    const weights = (treemapRankedData || [])
      .map((d: any) => Number(d?.relativeValueShare) || 0)
      .filter((w: number) => Number.isFinite(w) && w > 0)
      .map((w: number) => Math.log10(w + eps))
      .sort((a: number, b: number) => a - b);

    const percentile = (sorted: number[], p: number) => {
      if (!sorted.length) return 0;
      const pp = Math.max(0, Math.min(1, Number(p) || 0));
      const idx = (sorted.length - 1) * pp;
      const lo = Math.floor(idx);
      const hi = Math.ceil(idx);
      if (lo === hi) return sorted[lo];
      const t = idx - lo;
      return sorted[lo] + (sorted[hi] - sorted[lo]) * t;
    };

    const p05 = percentile(weights, 0.05);
    const p95 = percentile(weights, 0.95);

    return {
      p05: Number.isFinite(p05) ? p05 : 0,
      p95: Number.isFinite(p95) ? p95 : 0,
      eps,
    };
  }, [treemapRankedData]);

  const coloredTreemapData = useMemo(() => {
    const gray = '#9ca3af';
    const clamp = (x: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, x));
    const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
    const hexToRgb = (c: string) => [parseInt(c.slice(1, 3), 16), parseInt(c.slice(3, 5), 16), parseInt(c.slice(5, 7), 16)];
    const mix = (c1: string, c2: string, t: number) => {
      const [r1, g1, b1] = hexToRgb(c1);
      const [r2, g2, b2] = hexToRgb(c2);
      const r = Math.round(lerp(r1, r2, t));
      const g = Math.round(lerp(g1, g2, t));
      const b = Math.round(lerp(b1, b2, t));
      const toHex = (v: number) => v.toString(16).padStart(2, '0');
      return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    };
    const relLuminance = (hex: string) => {
      try {
        const [r8, g8, b8] = hexToRgb(hex);
        const srgbToLin = (u8: number) => {
          const u = clamp(u8 / 255, 0, 1);
          return u <= 0.04045 ? (u / 12.92) : Math.pow((u + 0.055) / 1.055, 2.4);
        };
        const r = srgbToLin(r8);
        const g = srgbToLin(g8);
        const b = srgbToLin(b8);
        return 0.2126 * r + 0.7152 * g + 0.0722 * b;
      } catch {
        return 0;
      }
    };
    const contrastRatio = (l1: number, l2: number) => {
      const hi = Math.max(l1, l2);
      const lo = Math.min(l1, l2);
      return (hi + 0.05) / (lo + 0.05);
    };
    const labelStyleForBg = (bgHex: string) => {
      const L = relLuminance(bgHex);
      const cWhite = contrastRatio(L, 1);
      const cBlack = contrastRatio(L, 0);
      const useWhite = cWhite >= cBlack;
      const color = useWhite ? '#ffffff' : 'rgba(0,0,0,0.87)';
      // Add a subtle outline for readability across mid-tones.
      const textBorderColor = useWhite ? 'rgba(0,0,0,0.45)' : 'rgba(255,255,255,0.45)';
      const textBorderWidth = 2;
      return { color, textBorderColor, textBorderWidth, fontWeight: 600 };
    };
    const viridisFromT = (t: number) => {
      const tt = clamp(Number(t) || 0, 0, 1);
      const stops: Array<[number, string]> = [
        // Reversed Viridis: yellow (low) -> purple (high), matching Sankey convention
        [0.0, '#fde725'],
        [0.25, '#5ec962'],
        [0.5, '#21918c'],
        [0.75, '#3b528b'],
        [1.0, '#440154'],
      ];
      for (let i = 0; i < stops.length - 1; i++) {
        const [t0, c0] = stops[i];
        const [t1, c1] = stops[i + 1];
        if (tt <= t1) {
          const u = (t1 - t0) > 0 ? (tt - t0) / (t1 - t0) : 0;
          return mix(c0, c1, clamp(u, 0, 1));
        }
      }
      return stops[stops.length - 1][1];
    };

    const lo = Number(treemapPathWeightStats?.p05) || 0;
    const hi = Number(treemapPathWeightStats?.p95) || 0;
    const denom = (hi - lo) || 1;
    const eps = Number(treemapPathWeightStats?.eps) || 1e-6;

    return (treemapRankedData || []).map((d: any) => {
      const s = Number(d?.relativeValueShare) || 0;
      if (!Number.isFinite(s) || s <= 0) {
        const ls = labelStyleForBg(gray);
        return {
          ...d,
          itemStyle: { ...(d?.itemStyle || {}), color: gray },
          label: { ...(d?.label || {}), ...ls }
        };
      }
      const lv = Math.log10(s + eps);
      const t = clamp((clamp(lv, lo, hi) - lo) / denom, 0, 1);
      const bg = viridisFromT(t);
      const ls = labelStyleForBg(bg);
      return {
        ...d,
        itemStyle: { ...(d?.itemStyle || {}), color: bg },
        label: { ...(d?.label || {}), ...ls }
      };
    });
  }, [treemapRankedData, treemapPathWeightStats]);

  const treemapOption = useMemo(() => {
    const hsLabel = treemapLevel === 'section' ? 'Section' : treemapLevel === 6 ? 'HS6' : treemapLevel === 4 ? 'HS4' : 'HS2';
    const truncate = (s: string, n: number) => {
      const t = (s || '').replace(/\s+/g, ' ').trim();
      if (!t) return '';
      return t.length > n ? t.slice(0, Math.max(0, n - 1)) + '…' : t;
    };
    const wrapLabelText = (s: string, lineLen: number, maxLines: number) => {
      const text = (s || '').replace(/\s+/g, ' ').trim();
      if (!text) return '';
      const words = text.split(' ');
      const lines: string[] = [];
      let cur = '';
      for (const w of words) {
        const next = cur ? `${cur} ${w}` : w;
        if (next.length <= lineLen) {
          cur = next;
          continue;
        }
        if (cur) lines.push(cur);
        cur = w;
        if (lines.length >= maxLines) break;
      }
      if (lines.length < maxLines && cur) lines.push(cur);
      const used = lines.join(' ');
      const hasMore = used.length < text.length;
      if (hasMore && lines.length > 0) {
        lines[lines.length - 1] = truncate(lines[lines.length - 1], Math.max(4, lineLen - 1));
      }
      return lines.slice(0, maxLines).join('\n');
    };
    const descForHs6 = (code6: string) => ((hsDescriptionsByCode as any)?.[normalizeHS(code6)] || {}) as any;
    const sectionLineForHs6 = (code6: string, maxLen = 220) => {
      const d = descForHs6(code6);
      const secRaw = d?.section != null ? String(d.section).trim() : '';
      const sec = normalizeSectionCode(secRaw);
      const secDescRaw = d?.section_description != null ? String(d.section_description) : '';
      const secDesc = secDescRaw ? truncate(softenAllCaps(secDescRaw), maxLen) : '';
      if (!sec && !secDesc) return '';
      if (sec && secDesc) return `Section ${sec}: ${secDesc}`;
      if (sec) return `Section ${sec}`;
      return `Section ${secDesc}`;
    };
    const sectionLineHtmlForHs6 = (code6: string, maxLen = 220) => {
      const d = descForHs6(code6);
      const secRaw = d?.section != null ? String(d.section).trim() : '';
      const sec = normalizeSectionCode(secRaw);
      const secDescRaw = d?.section_description != null ? String(d.section_description) : '';
      const secDesc = secDescRaw ? truncate(softenAllCaps(secDescRaw), maxLen) : '';
      if (!sec && !secDesc) return '';
      if (sec) return `<b>Section ${sec}</b>${secDesc ? `: ${secDesc}` : ''}`;
      return `Section ${secDesc}`;
    };
    const desc2ForBucket = (hs6Items: Array<{ code: string }> = []) => {
      const first = hs6Items?.[0]?.code ? normalizeHS(hs6Items[0].code) : '';
      const d = first ? descForHs6(first) : {};
      return d?.description_2 ? truncate(String(d.description_2), 220) : '';
    };
    const desc4ForBucket = (hs6Items: Array<{ code: string }> = []) => {
      const first = hs6Items?.[0]?.code ? normalizeHS(hs6Items[0].code) : '';
      const d = first ? descForHs6(first) : {};
      return d?.description_4 ? truncate(String(d.description_4), 220) : '';
    };
    const sectionForBucket = (hs6Items: Array<{ code: string }> = []) => {
      const first = hs6Items?.[0]?.code ? normalizeHS(hs6Items[0].code) : '';
      return first ? sectionLineForHs6(first, 220) : '';
    };
    const sectionHtmlForBucket = (hs6Items: Array<{ code: string }> = []) => {
      const first = hs6Items?.[0]?.code ? normalizeHS(hs6Items[0].code) : '';
      return first ? sectionLineHtmlForHs6(first, 220) : '';
    };
    const sectionDescForBucket = (hs6Items: Array<{ code: string }> = []) => {
      const first = hs6Items?.[0]?.code ? normalizeHS(hs6Items[0].code) : '';
      if (!first) return '';
      const d = descForHs6(first);
      const secDescRaw = d?.section_description != null ? String(d.section_description) : '';
      return secDescRaw ? truncate(softenAllCaps(secDescRaw), 220) : '';
    };
    const maxTreemapValue = Math.max(1, ...((coloredTreemapData || []).map((d: any) => Number(d?.value) || 0)));
    const detailLevelForLabel = (p: any): 'code' | 'compact' | 'full' => {
      const v = Number(p?.data?.value) || 0;
      const ratio = v / maxTreemapValue;
      if (ratio < 0.03) return 'code';
      if (ratio < 0.06) return 'compact';
      return 'full';
    };
    return {
      tooltip: {
        trigger: 'item',
        renderMode: 'html',
        confine: true,
        // Keep tooltip readable on mobile and prevent viewport overflow; allow scrolling for long HS6 lists
        extraCssText: [
          'max-width: min(92vw, 560px)',
          'max-height: min(60vh, 520px)',
          'overflow: auto',
          'white-space: normal',
          'word-break: break-word',
          'line-height: 1.25',
          `font-size: ${isMobile ? 12 : 13}px`,
          'padding: 10px 12px',
        ].join('; '),
        position: (pos: any, _params: any, _dom: any, _rect: any, size: any) => {
          // pos is [x, y] in pixels relative to viewport
          let x = Array.isArray(pos) ? Number(pos[0]) : 0;
          let y = Array.isArray(pos) ? Number(pos[1]) : 0;
          const viewW = Number(size?.viewSize?.[0]) || 0;
          const viewH = Number(size?.viewSize?.[1]) || 0;
          const boxW = Number(size?.contentSize?.[0]) || 0;
          const boxH = Number(size?.contentSize?.[1]) || 0;
          const pad = 8;
          if (viewW && boxW && x + boxW + pad > viewW) x = viewW - boxW - pad;
          if (viewH && boxH && y + boxH + pad > viewH) y = viewH - boxH - pad;
          if (x < pad) x = pad;
          if (y < pad) y = pad;
          return [x, y];
        },
        formatter: (p: any) => {
          const code = p?.name ?? '—';
          const expTot = Number(p?.data?.exportsValue);
          const impTot = Number(p?.data?.importsValue);
          const exportsValue = Number.isFinite(expTot) ? expTot : 0;
          const importsValue = Number.isFinite(impTot) ? impTot : 0;
          const rank = p?.data?.rank != null ? Number(p.data.rank) : null;
          const share = Number(p?.data?.relativeValueShare) || 0;
          if (treemapLevel === 6) {
            const code6 = normalizeHS(code);
            const d = (hsDescriptionsByCode as any)?.[code6] || {};
            const code2 = code6 ? String(code6).slice(0, 2) : '';
            const code4 = code6 ? String(code6).slice(0, 4) : '';
            // Show more text before truncating in tooltips
            const d2 = d?.description_2 ? truncate(String(d.description_2), 220) : '';
            const d4 = d?.description_4 ? truncate(String(d.description_4), 220) : '';
            const d6 = d?.description_6 ? truncate(String(d.description_6), 320) : '';
            const secLineHtml = sectionLineHtmlForHs6(code6, 220);
            // HS6 tooltip: show BOTH exports and imports; sizing still controlled by treemapFlow.
            const expFallback = Number((hsTradeMap as any)?.[code6]?.exports);
            const impFallback = Number((hsTradeMap as any)?.[code6]?.imports);
            // Prefer treemapData-provided values when present (works for treemap items), else fall back to worldSummary-derived maps if any.
            const e = Number.isFinite(exportsValue) && exportsValue > 0 ? exportsValue : (Number.isFinite(expFallback) ? expFallback : 0);
            const i = Number.isFinite(importsValue) && importsValue > 0 ? importsValue : (Number.isFinite(impFallback) ? impFallback : 0);
            const pw = Number((hsPathWeightMap as any)?.[code6] || 0);
            const shareFallback = (treemapTotalUniverseWeight > 0 && Number.isFinite(pw) && pw > 0) ? (pw / treemapTotalUniverseWeight) : 0;
            const s = share > 0 ? share : shareFallback;
            const lines = [
              `<div><b>${hsLabel}:</b> ${code6}</div>`,
              (code6 && d6) ? `<div><b>${code6}:</b> ${d6}</div>` : '',
              (code4 && d4) ? `<div><b>${code4}:</b> ${d4}</div>` : '',
              (code2 && d2) ? `<div><b>${code2}:</b> ${d2}</div>` : '',
              secLineHtml ? `<div>${secLineHtml}</div>` : '',
              `<div style="margin-top:8px"><b>Relative value share:</b> ${(Number(s) * 100).toFixed(2)}%${rank ? ` <span style="opacity:0.7">(rank ${rank})</span>` : ''}</div>`,
              `<div style="margin-top:8px"><b>Exports:</b> ${fmt2.format(Number(e) || 0)} USD thousands</div>`,
              `<div><b>Imports:</b> ${fmt2.format(Number(i) || 0)} USD thousands</div>`,
            ].filter(Boolean).join('');
            return `<div>${lines}</div>`;
          }
          const items: Array<{ code: string; description: string; value: number }> = Array.isArray(p?.data?.hs6Items) ? p.data.hs6Items : [];
          // Show more HS6 rows in tooltip before collapsing
          const maxItems = isMobile ? 18 : 40;
          const shown = items.slice(0, maxItems);
          const remaining = Math.max(0, items.length - shown.length);
          // Add HS2/HS4 descriptions for aggregated levels
          const headerLines: string[] = [];
          if (treemapLevel === 4) {
            const code4 = String(code || '').slice(0, 4);
            const code2 = code4.slice(0, 2);
            const d2 = desc2ForBucket(items);
            const d4 = desc4ForBucket(items);
            // Keep ordering consistent with labels: HS4 → HS2 → Section
            if (code4 && d4) headerLines.push(`<div><b>${code4}:</b> ${d4}</div>`);
            if (code2 && d2) headerLines.push(`<div><b>${code2}:</b> ${d2}</div>`);
          } else if (treemapLevel === 2) {
            const code2 = String(code || '').slice(0, 2);
            const d2 = desc2ForBucket(items);
            if (code2 && d2) headerLines.push(`<div><b>${code2}:</b> ${d2}</div>`);
          } else if (treemapLevel === 'section') {
            const secDesc = sectionDescForBucket(items);
            if (secDesc) headerLines.push(`<div>${secDesc}</div>`);
          }
          // Section line goes last (except when the current bucket IS Section)
          if (treemapLevel !== 'section') {
            const secHtml = sectionHtmlForBucket(items);
            if (secHtml) headerLines.push(`<div>${secHtml}</div>`);
          }
          const lines = shown.map(it => {
            const c = it.code;
            const d = truncate(String(it.description || ''), 160);
            const ev = Number((it as any).exportsValue) || 0;
            const iv = Number((it as any).importsValue) || 0;
            const label = d ? `${c} — ${d}` : `${c}`;
            return `${label} (exp ${fmt2.format(ev)} / imp ${fmt2.format(iv)})`;
          });
          const more = remaining > 0 ? `… +${remaining} more` : '';
          const listHtml = lines.length ? `<div style="margin-top:10px"><b>Included HS6 codes</b><div style="margin-top:6px">${lines.join('<br/>')}${more ? `<br/>${more}` : ''}</div></div>` : '';
          const totalExp = Number.isFinite(exportsValue) ? exportsValue : (items || []).reduce((s, it) => s + (Number((it as any).exportsValue) || 0), 0);
          const totalImp = Number.isFinite(importsValue) ? importsValue : (items || []).reduce((s, it) => s + (Number((it as any).importsValue) || 0), 0);
          const s = Number(share) || 0;
          return `<div><div><b>${hsLabel}:</b> ${code}</div>${headerLines.join('')}<div style="margin-top:8px"><b>Relative value share:</b> ${(Number(s) * 100).toFixed(2)}%${rank ? ` <span style="opacity:0.7">(rank ${rank})</span>` : ''}</div><div style="margin-top:8px"><b>Exports:</b> ${fmt2.format(Number(totalExp) || 0)} USD thousands</div><div><b>Imports:</b> ${fmt2.format(Number(totalImp) || 0)} USD thousands</div>${listHtml}</div>`;
        }
      },
      labelLayout: { hideOverlap: false },
      series: [
        {
          type: 'treemap',
          roam: false,
          nodeClick: false,
          sort: 'desc',
          squareRatio: 0.6,
          visibleMin: 20,
          breadcrumb: { show: false },
          itemStyle: {
            borderColor: theme.palette.divider,
            borderWidth: 1,
            gapWidth: 1,
          },
          label: {
            show: true,
            color: theme.palette.text.primary,
            position: 'insideTopLeft',
            align: 'left',
            verticalAlign: 'top',
            padding: [4, 5, 2, 5],
            overflow: 'break',
            lineHeight: 16,
            formatter: (p: any) => {
              const code = p?.name ?? '';
              const detail = detailLevelForLabel(p);
              if (treemapLevel === 'section') {
                const items: Array<{ code: string }> = Array.isArray(p?.data?.hs6Items) ? p.data.hs6Items : [];
                const secDesc = sectionDescForBucket(items);
                const labelCode = code ? `Section ${code}` : 'Section';
                if (detail === 'code') return labelCode;
                if (detail === 'compact') return [labelCode, secDesc ? truncate(secDesc, 36) : ''].filter(Boolean).join('\n');
                const parts = [
                  labelCode,
                  secDesc ? wrapLabelText(secDesc, 38, 5) : '',
                ].filter(Boolean);
                return parts.join('\n');
              }
              if (treemapLevel === 4) {
                const code4 = String(code || '').slice(0, 4);
                const items: Array<{ code: string }> = Array.isArray(p?.data?.hs6Items) ? p.data.hs6Items : [];
                const d2 = desc2ForBucket(items);
                const d4 = desc4ForBucket(items);
                if (detail === 'code') return `${code4}`;
                if (detail === 'compact') {
                  const first = d4 || d2;
                  return first ? `${code4}\n${truncate(first, 34)}` : `${code4}`;
                }
                const parts = [
                  d4 ? wrapLabelText(d4, 34, 3) : '',
                  d2 ? wrapLabelText(d2, 34, 2) : '',
                ].filter(Boolean);
                return parts.length ? `${code4}\n${parts.join('\n')}` : `${code4}`;
              }
              if (treemapLevel === 2) {
                const code2 = String(code || '').slice(0, 2);
                const items: Array<{ code: string }> = Array.isArray(p?.data?.hs6Items) ? p.data.hs6Items : [];
                const d2 = desc2ForBucket(items);
                if (detail === 'code') return `${code2}`;
                if (detail === 'compact') return d2 ? `${code2}\n${truncate(d2, 38)}` : `${code2}`;
                return d2 ? `${code2}\n${wrapLabelText(d2, 38, 6)}` : `${code2}`;
              }
              if (treemapLevel !== 6) return `${code}`;
              const code6 = normalizeHS(code);
              const d = (hsDescriptionsByCode as any)?.[code6] || {};
              const d2Raw = d?.description_2 ? String(d.description_2) : '';
              const d4Raw = d?.description_4 ? String(d.description_4) : '';
              const d6Raw = d?.description_6 ? String(d.description_6) : '';
              if (detail === 'code') return `${code6}`;
              if (detail === 'compact') {
                const first = d6Raw || d4Raw || d2Raw;
                return first ? `${code6}\n${truncate(first, 34)}` : `${code6}`;
              }
              const parts = [
                d6Raw ? wrapLabelText(d6Raw, 34, 4) : '',
                d4Raw ? wrapLabelText(d4Raw, 34, 2) : '',
                d2Raw ? wrapLabelText(d2Raw, 34, 2) : '',
              ].filter(Boolean);
              return parts.length ? `${code6}\n${parts.join('\n')}` : `${code6}`;
            }
          },
          upperLabel: { show: false },
          levels: [
            {
              itemStyle: { borderWidth: 1, gapWidth: 1 }
            }
          ],
          data: coloredTreemapData,
        }
      ]
    } as any;
  }, [coloredTreemapData, treemapFlow, treemapLevel, fmt2, fmt3, theme.palette.divider, theme.palette.text.primary, hsDescriptionsByCode, hsDescriptionMap, hsPathWeightMap, treemapTotalUniverseWeight, isMobile]);

  const handleTreemapClick = useCallback((params: any) => {
    const code = String(params?.name || '').trim();
    if (!code) return;
    const expTot = Number(params?.data?.exportsValue);
    const impTot = Number(params?.data?.importsValue);
    const exportsTotal = Number.isFinite(expTot) ? expTot : 0;
    const importsTotal = Number.isFinite(impTot) ? impTot : 0;
    const normalizeHSCandidate = (v: any) => {
      if (v == null) return '';
      const s = String(v);
      const m = s.match(/(\d{6})/);
      if (m) return m[1];
      const digits = s.replace(/\D/g, '');
      if (digits.length > 0) return digits.padStart(6, '0').slice(-6);
      return s;
    };
    // For HS6, pin the single code + its name; for HS4/HS2, pin the list of HS6 children.
    if (treemapLevel === 6) {
      const code6 = normalizeHSCandidate(code);
      const d = (hsDescriptionsByCode as any)?.[code6] || {};
      const desc = d?.description_6 != null ? String(d.description_6) : '';
      const expFallback = Number((hsTradeMap as any)?.[code6]?.exports);
      const impFallback = Number((hsTradeMap as any)?.[code6]?.imports);
      const e = exportsTotal > 0 ? exportsTotal : (Number.isFinite(expFallback) ? expFallback : 0);
      const i = importsTotal > 0 ? importsTotal : (Number.isFinite(impFallback) ? impFallback : 0);
      setTreemapSelection({
        level: 6,
        flow: treemapFlow,
        code: code6,
        exportsTotal: e,
        importsTotal: i,
        hs6Items: [{ code: code6, description: desc, value: (treemapFlow === 'exports' ? e : i), exportsValue: e, importsValue: i }],
      });
      return;
    }
    const hs6Items: Array<{ code: string; description: string; value: number; exportsValue: number; importsValue: number }> =
      Array.isArray(params?.data?.hs6Items) ? params.data.hs6Items : [];
    setTreemapSelection({
      level: treemapLevel,
      flow: treemapFlow,
      code,
      exportsTotal,
      importsTotal,
      hs6Items: hs6Items.slice(),
    });
  }, [treemapLevel, hsDescriptionsByCode, hsTradeMap, treemapFlow]);
  const treemapEvents = useMemo(() => ({ click: handleTreemapClick }), [handleTreemapClick]);

  // Clear any pinned treemap selection when the view changes
  useEffect(() => {
    setTreemapSelection(null);
    // Keep the panel compact by default when switching views/levels.
    setTreemapInfoExpanded(false);
  }, [treemapFlow, treemapLevel, selectedCountry, selectedYear, selectedElementId]);

  // Stable empty objects to prevent unnecessary re-renders
  const emptyTradeMap = useMemo(() => ({}), []);
  const emptyRcaMap = useMemo(() => ({}), []);

  // Memoized title to prevent re-renders
  const diagramTitle = useMemo(() => {
    const rootName = (Array.isArray(availableRoots) && availableRoots.find(n => n.element_id === selectedElementId))?.name || 'Selected';
    const modeLabel =
      colorMode === 'rca' ? 'RCA' :
      colorMode === 'exports' ? 'exports' :
      colorMode === 'imports' ? 'imports' :
      'balance';
    const countrySuffix = selectedCountry && selectedCountryName !== '—' ? ` — ${selectedCountryName}` : '';
    const suffix = selectedCountry && selectedYear ? ` (${modeLabel})` : '';
    return `${rootName} Value Chain in HS Codes${countrySuffix}${suffix}`;
  }, [availableRoots, selectedElementId, selectedCountry, selectedCountryName, selectedYear, colorMode]);

  // Memoized onNodeClick handler
  const handleNodeClick = useMemo(() => (params) => {
    const nd = params?.data?.nodeData;
    const props = nd?.properties || {};
    const members = Array.isArray(props.members) ? props.members : [];
    setSelectedGroup({ name: params?.name, description: props.hs_code_description || '', count: members.length });
    setSelectedMembers(members);
  }, []);

  // Memoized legend component to prevent re-renders
  const diagramLegend = useMemo(() => {
    const Swatch = ({ color }) => (
      <Box sx={{ width: 28, height: 6, borderRadius: 3, bgcolor: color, opacity: 0.45, border: '1px solid', borderColor: (t) => t.palette.divider, mr: 1 }} />
    );
    const computeFlowLegendColor = () => {
      try {
        const links = (graphData && Array.isArray(graphData.links)) ? graphData.links : [];
        if (links.length === 0) return '#42a5f5';
        const l = links[0] || {};
        let c = l?.lineStyle?.color;
        if (!c || c === 'source') {
          const nodes = (graphData && Array.isArray(graphData.nodes)) ? graphData.nodes : [];
          const src = nodes.find(n => n && n.name === l.source);
          c = src?.itemStyle?.color || '#42a5f5';
        }
        return c;
      } catch { return '#42a5f5'; }
    };
    return (
      <Box sx={{ mt: 1 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr', md: '3fr 1fr' }, gap: 1.5 }}>
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block' }}>Legend (flows)</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Each component or material can appear in the form of several technology types, with a prevalence weight that tells us how common that type is.

              Each technology type is made up of different components/materials, each with an estimated cost-share weight that approximates the relative cost share of that component in the mix of other components, materials, labor, etc.

              When we connect one technology type to another through a component (Tech A → Component → Tech B), we calculate a path weight by multiplying the prevalence (from Tech A to the component) with the cost share (from the component to Tech B).

              Every technology type is assigned to a 6-digit HS code following the recommendations of the <Link href="https://www.wcotradetools.org/en/harmonized-system/rules" target="_blank" rel="noopener noreferrer">General rules for the interpretation of the harmonized system</Link>.

              Finally, for each HS code, we add up the contributions from all technology type paths mapped to it. This gives the flow weights shown in the diagram.
            </Typography>
          </Box>
          <Paper elevation={0} variant="outlined" sx={{ p: 1, bgcolor: 'rgba(255,255,255,0.5)', borderColor: (t) => t.palette.divider }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Flow Weights</Typography>
            <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center' }}>
              <Swatch color={computeFlowLegendColor()} />
              <Typography variant="caption" color="text.secondary">HS Code Flow (Sum of Technology Path Weights with the same HS Codes)</Typography>
            </Box>
          </Paper>
        </Box>
      </Box>
    );
  }, [graphData]);

  // Color scale helper
  const colorForNorm = (n) => {
    const clamp = (x, lo, hi) => Math.max(lo, Math.min(hi, x));
    const lerp = (a, b, t) => a + (b - a) * t;
    const mix = (c1, c2, t) => {
      const h = (c) => [parseInt(c.slice(1,3),16), parseInt(c.slice(3,5),16), parseInt(c.slice(5,7),16)];
      const [r1,g1,b1] = h(c1); const [r2,g2,b2] = h(c2);
      const r = Math.round(lerp(r1,r2,t));
      const g = Math.round(lerp(g1,g2,t));
      const b = Math.round(lerp(b1,b2,t));
      const toHex = (v)=> v.toString(16).padStart(2,'0');
      return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    };
    const nn = clamp(Number(n) || 0, -1, 1);
    if (nn < 0) return mix('#d32f2f', '#fbc02d', nn + 1);
    return mix('#fbc02d', '#2e7d32', nn);
  };
  const colorForTrade = (value: number, cap: number) => {
    const clamp = (x, lo, hi) => Math.max(lo, Math.min(hi, x));
    const lerp = (a, b, t) => a + (b - a) * t;
    const mix = (c1, c2, t) => {
      const h = (c) => [parseInt(c.slice(1,3),16), parseInt(c.slice(3,5),16), parseInt(c.slice(5,7),16)];
      const [r1,g1,b1] = h(c1); const [r2,g2,b2] = h(c2);
      const r = Math.round(lerp(r1,r2,t));
      const g = Math.round(lerp(g1,g2,t));
      const b = Math.round(lerp(b1,b2,t));
      const toHex = (v)=> v.toString(16).padStart(2,'0');
      return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    };
    const v = Math.max(0, Number(value) || 0);
    const c = Math.max(0, Number(cap) || 0);
    const t = c > 0 ? (v / c) : 0;
    const tt = clamp(t, 0, 1);
    const stops: Array<[number, string]> = [
      // Reversed Viridis: yellow -> blue/purple
      [0.0, '#fde725'],
      [0.25, '#5ec962'],
      [0.5, '#21918c'],
      [0.75, '#3b528b'],
      [1.0, '#440154'],
    ];
    for (let i = 0; i < stops.length - 1; i++) {
      const [t0, c0] = stops[i];
      const [t1, c1] = stops[i + 1];
      if (tt <= t1) {
        const u = (t1 - t0) > 0 ? (tt - t0) / (t1 - t0) : 0;
        return mix(c0, c1, clamp(u, 0, 1));
      }
    }
    return stops[stops.length - 1][1];
  };
  const rcaToNorm = (rca) => {
    if (rca == null) return 0;
    const eps = 1e-6;
    const base = Number(rcaUpper) > 1 ? Number(rcaUpper) : 10;
    const n = Math.log(Math.max(Number(rca), eps)) / Math.log(base);
    return Math.max(-1, Math.min(1, n));
  };

  // DEVELOPMENT MODE: Load all value chains from API instead of permission-filtered
  // TODO: Restore permission-based filtering when re-enabling permissions
  useEffect(() => {
    // Load all available roots from API with authentication
    (async () => {
      try {
        if (SHOULD_DEBUG) console.log('Fetching roots, isAuthenticated:', isAuthenticated);
        const response = await api.graphData.getAvailableRootNodesApiV1GraphRootsGet();
        if (SHOULD_DEBUG) console.log('Raw roots API response:', response);
        
        // API returns { success: true, data: [...], message: "..." }
        // Extract the actual data array
        const roots = Array.isArray(response?.data) ? response.data : 
                     Array.isArray(response) ? response : [];
        
        if (SHOULD_DEBUG) {
          console.log('Processed roots:', roots);
          console.log('Number of roots:', roots.length);
        }
        setAvailableRoots(roots);
        
        // If current selection is no longer available (e.g., after logout), clear it and select first public one
        if (selectedElementId) {
          const isStillAvailable = roots.some((r:any) => String(r.element_id) === String(selectedElementId));
          if (!isStillAvailable) {
            if (SHOULD_DEBUG) console.log('Selected root no longer available, selecting first available public root');
            setSelectedElementId('');
            setSelectedRootElementId('');
            setSelectedRootName('');
            setSelectedRootSourceDb('');
            setGraphData(null);
            // Automatically select first available public root
            if (roots.length > 0) {
              const firstPublic = roots[0];
              if (SHOULD_DEBUG) console.log('Auto-selecting first available root after logout:', firstPublic);
              setSelectedElementId(String(firstPublic.element_id));
              setSelectedRootElementId(String(firstPublic.element_id));
              setSelectedRootName(String(firstPublic.name));
              setSelectedRootSourceDb(String(firstPublic.source_db || ''));
            }
          }
        }
        
        const savedId = String(localStorage.getItem(STORAGE_ID_KEY) || '');
        const savedName = String(localStorage.getItem(STORAGE_NAME_KEY) || '');
        const savedSourceDb = String(localStorage.getItem(STORAGE_SOURCE_DB_KEY) || '');
        if (!selectedElementId) {
          if (savedId) {
            const matchedRoot = roots.find((r:any)=>String(r.element_id)===savedId);
            if (matchedRoot) {
              setSelectedElementId(String(savedId));
              setSelectedRootElementId(String(savedId));
              setSelectedRootName(savedName || (matchedRoot?.name || ''));
              setSelectedRootSourceDb(savedSourceDb || (matchedRoot?.source_db || ''));
            } else if (roots.length > 0) {
              // If saved root not available, select first available
              const first = roots[0];
              if (SHOULD_DEBUG) console.log('Saved root not available, selecting first available:', first);
              setSelectedElementId(String(first.element_id));
              setSelectedRootElementId(String(first.element_id));
              setSelectedRootName(String(first.name));
              setSelectedRootSourceDb(String(first.source_db || ''));
            }
          } else if (roots.length > 0) {
            const first = roots[0];
            if (SHOULD_DEBUG) console.log('Setting first root:', first);
            setSelectedElementId(String(first.element_id));
            setSelectedRootElementId(String(first.element_id));
            setSelectedRootName(String(first.name));
            setSelectedRootSourceDb(String(first.source_db || ''));
          }
        }
      } catch (error) {
        if (SHOULD_DEBUG) console.error('Error loading roots:', error);
        setAvailableRoots([]);
      }
    })();
  }, [isAuthenticated]); // Reload when authentication state changes

  /* ORIGINAL PERMISSION-BASED CODE - Uncomment to restore
  useEffect(() => {
    if (accessibleValueChains && accessibleValueChains.length > 0) {
      if (SHOULD_DEBUG) console.log('Setting available roots from accessible value chains:', accessibleValueChains);
      setAvailableRoots(accessibleValueChains);
      if (!selectedElementId && accessibleValueChains.length > 0) {
        const first = accessibleValueChains[0];
        setSelectedElementId(String(first.element_id));
      }
    } else {
      setAvailableRoots([]);
    }
  }, [accessibleValueChains, selectedElementId]);
  */

  useEffect(() => {
    // Load countries and available HS years (from hs_code_versions.available = True)
    (async () => {
      try {
        const [countriesResp, yearsResp] = await Promise.all([
          api.countries.listCountriesApiV1CountriesGet(),
          api.trade.listTradeYearsApiV1TradeYearsGet()
        ]);
        const countriesList = Array.isArray(countriesResp) ? countriesResp : [];
        if (SHOULD_DEBUG) {
          console.log('Loaded countries:', countriesList);
          console.log('Loaded years (raw):', yearsResp);
        }
        
        // Handle both formats: array of numbers [2020, 2021] or array of objects [{year: 2020}]
        const yearsList = Array.isArray(yearsResp) ? yearsResp.map(y => {
          if (typeof y === 'object' && y !== null && 'year' in y) {
            return y; // Already in correct format
          } else {
            return { year: Number(y) }; // Convert number to object format
          }
        }) : [];
        
        if (SHOULD_DEBUG) console.log('Processed years:', yearsList);
        setCountries(countriesList);
        setYears(yearsList);

        // Default country in HS Code view: Romania (ISO numeric 642).
        const defaultRomania = countriesList.find((c: any) => {
          const code = String(c?.country_code ?? c?.code ?? '');
          const name = String(c?.country_name ?? c?.name ?? '').toLowerCase();
          return code === '642' || name === 'romania';
        });
        if (defaultRomania) {
          const romaniaCode = String(defaultRomania?.country_code ?? defaultRomania?.code ?? '');
          if (romaniaCode) setSelectedCountry(romaniaCode);
        }
        
        // Select the latest year by default
        if (yearsList.length > 0) {
          const latestYear = yearsList[yearsList.length - 1];
          const yearValue = latestYear.year;
          setSelectedYear(String(yearValue));
          if (SHOULD_DEBUG) console.log('Selected year:', yearValue);
        }
      } catch (e) {
        if (SHOULD_DEBUG) console.error('Error fetching countries/years:', e);
      }
    })();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      if (!selectedElementId) { if (!cancelled) setGraphData(null); return; }
      
      setLoading(true); setError(null);
      try {
        const resp = await api.graphData.getIncomingGroupedByHsApiV1GraphIncomingGroupedHsElementIdGet(String(selectedElementId), maxIterations, minFlowThreshold);
        if (!resp || !resp.success || !resp.data) { if (!cancelled) { setGraphData({ nodes: [], links: [] }); setSankeyHSCodes([]); } return; }
        const data = resp.data;
        // Build nodes and links in local variables
        const idToName = new Map();
        const nodes = (data.nodes || []).map(n => {
          const label = n.properties?.name || n.id;
          idToName.set(n.id, label);
          const match = typeof label === 'string' ? label.match(/^\s*(\d+)\s*:/) : null;
          const nIteration = match ? parseInt(match[1], 10) : undefined;
          return { name: label, nodeData: { type: 'Grouped', elementId: n.id, n_iteration: nIteration, properties: n.properties || {} } };
        });
        const links = (data.relationships || []).map(r => ({ source: idToName.get(r.start_node_id) || r.start_node_id, target: idToName.get(r.end_node_id) || r.end_node_id, value: r.properties?.path_weight ?? 0 }));

        // Extract HS codes
        const hsSet = new Set();
        const sixDigit = /\b(\d{6})\b/;
        (data.nodes || []).forEach(n => {
          const p = n.properties || {};
          const hs = p.hs_code;
          if (Array.isArray(hs)) { hs.forEach(code => { if (typeof code === 'string') { const m = code.match(sixDigit); if (m) hsSet.add(m[1]); } }); }
          else if (typeof hs === 'string') { const m = hs.match(sixDigit); if (m) hsSet.add(m[1]); }
          const label = typeof p.name === 'string' ? p.name : (typeof n.properties?.name === 'string' ? n.properties.name : n.id);
          if (typeof label === 'string') { const m2 = label.match(sixDigit); if (m2) hsSet.add(m2[1]); }
        });
        if (!cancelled) {
          setGraphData({ nodes, links });
          setSankeyHSCodes(Array.from(hsSet) as string[]);
        }
      } catch (e) {
        if (SHOULD_DEBUG) console.error(e);
        if (!cancelled) { setError(e.response?.data?.detail || 'Failed to fetch grouped HS graph'); setSankeyHSCodes([]); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [selectedElementId, maxIterations, minFlowThreshold, availableRoots, isAuthenticated]);

  // Refetch only when threshold decreases (expand results)
  const prevThresholdRef = useRef(minFlowThreshold);
  useEffect(() => {
    if (minFlowThreshold < prevThresholdRef.current && selectedElementId) {
      let cancelled = false;
      const fetchData = async () => {
        setLoading(true); setError(null);
        try {
          const resp = await api.graphData.getIncomingGroupedByHsApiV1GraphIncomingGroupedHsElementIdGet(String(selectedElementId), maxIterations, minFlowThreshold);
          if (!resp || !resp.success || !resp.data) { if (!cancelled) { setGraphData({ nodes: [], links: [] }); setSankeyHSCodes([]); } return; }
          const data = resp.data;
          const idToName = new Map();
          const nodes = (data.nodes || []).map(n => {
            const label = n.properties?.name || n.id;
            idToName.set(n.id, label);
            const match = typeof label === 'string' ? label.match(/^\s*(\d+)\s*:/) : null;
            const nIteration = match ? parseInt(match[1], 10) : undefined;
            return { name: label, nodeData: { type: 'Grouped', elementId: n.id, n_iteration: nIteration, properties: n.properties || {} } };
          });
          const links = (data.relationships || []).map(r => ({ source: idToName.get(r.start_node_id) || r.start_node_id, target: idToName.get(r.end_node_id) || r.end_node_id, value: r.properties?.path_weight ?? 0 }));
          const hsSet = new Set();
          const sixDigit = /\b(\d{6})\b/;
          (data.nodes || []).forEach(n => {
            const p = n.properties || {};
            const hs = p.hs_code;
            if (Array.isArray(hs)) { hs.forEach(code => { if (typeof code === 'string') { const m = code.match(sixDigit); if (m) hsSet.add(m[1]); } }); }
            else if (typeof hs === 'string') { const m = hs.match(sixDigit); if (m) hsSet.add(m[1]); }
            const label = typeof p.name === 'string' ? p.name : (typeof n.properties?.name === 'string' ? n.properties.name : n.id);
            if (typeof label === 'string') { const m2 = label.match(sixDigit); if (m2) hsSet.add(m2[1]); }
          });
          if (!cancelled) {
            setGraphData({ nodes, links });
            setSankeyHSCodes(Array.from(hsSet) as string[]);
          }
        } catch (e) {
          if (SHOULD_DEBUG) console.error(e);
          if (!cancelled) { setError(e.response?.data?.detail || 'Failed to fetch grouped HS graph'); setSankeyHSCodes([]); }
        } finally {
          if (!cancelled) setLoading(false);
        }
      };
      fetchData();
      return () => { cancelled = true; };
    }
    prevThresholdRef.current = minFlowThreshold;
  }, [minFlowThreshold, selectedElementId, maxIterations]);

  // Load WORLD summary when country/year change
  useEffect(() => {
    const loadWorldData = async () => {
      try {
        if (!selectedCountry || !selectedYear) { setWorldLoading(false); setWorldSummary(emptyWorldSummary); return; }
        if (!sankeyHSCodes || sankeyHSCodes.length === 0) { setWorldLoading(false); setWorldSummary(emptyWorldSummary); return; }
        setWorldLoading(true);
        // Fetch summary and RCA in parallel to reduce latency
        const [summary, rcaResp] = await Promise.all([
          hsCodeValueChainService.getCountryWorldSummary(selectedCountry, Number(selectedYear), sankeyHSCodes),
          hsCodeValueChainService.getRCA(selectedCountry, Number(selectedYear), sankeyHSCodes)
        ]);
        setWorldSummary(summary);
        const map = {};
        (rcaResp.items || []).forEach((it) => { if (it && it.hs_code) map[normalizeHS(it.hs_code)] = typeof it.rca === 'number' ? it.rca : null; });
        setRcaByCode(map);
      } catch (e) {
        if (SHOULD_DEBUG) console.error('Error fetching country-world summary:', e);
        setWorldSummary(emptyWorldSummary);
        setRcaByCode({});
      } finally { setWorldLoading(false); }
    };
    loadWorldData();
  }, [selectedCountry, selectedYear, sankeyHSCodes, emptyWorldSummary]);

  // DEVELOPMENT MODE: Permission checks commented out for public access
  // TODO: Uncomment these blocks to re-enable permission checks
  /*
  // Show loading state while permissions are loading
  if (permissionsLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading permissions...
        </Typography>
      </Box>
    );
  }

  // Show message if no accessible value chains
  if (!accessibleValueChains || accessibleValueChains.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Trade Code Analysis
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            No Value Chain Access
          </Typography>
          <Typography variant="body2">
            You don't have access to any value chains. Please contact your administrator to request access.
          </Typography>
        </Alert>
      </Box>
    );
  }
  */

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Trade Code Analysis
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'minmax(0,1fr) minmax(0,1fr)' }, gap: 2, mb: 3, minWidth: 0 }}>
        <Paper elevation={2} sx={{ p: 3, bgcolor: 'rgba(255,255,255,0.3)', border: '1px solid', borderColor: (t) => t.palette.divider, minWidth: 0, overflow: 'hidden' }}>
          <Typography variant="body1" gutterBottom>Select a Value Chain to View its HS Codes</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Click on a node to view what technology types are subsumed under this HS code.
          </Typography>
          <Divider sx={{ my: 2, opacity: 0.5 }} />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'minmax(0,2fr) minmax(0,1fr) minmax(0,1fr)' }, gap: 2, alignItems: 'end', minWidth: 0 }}>
          {/* Value chain + layers + threshold */}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" gutterBottom>
              Value Chain
            </Typography>
            <FormControl fullWidth size="small" sx={{ minWidth: 0 }}>
              <Select onChange={handleRootChange} displayEmpty renderValue={(val) => {
                const id = String(val || '');
                if (selectedRootElementId && id === String(selectedRootElementId) && selectedRootName) {
                  return selectedRootName;
                }
                const n = (availableRoots || []).find((x) => String(x.element_id) === id);
                return n ? n.name : '';
              }} MenuProps={{
                disableScrollLock: true,
                PaperProps: {
                  sx: {
                    maxHeight: { xs: '56vh', md: 320 },
                    width: { xs: 'min(92vw, 420px)', md: 'auto' }
                  }
                },
                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                transformOrigin: { vertical: 'top', horizontal: 'left' },
                MenuListProps: { dense: true }
              }}
                value={String(selectedRootElementId || '')}
              >
                {(() => {
                  const existingNames = new Set((availableRoots || []).map(n => n.name));
                  const placeholders = Array.from(disabledValueChainNames).filter(n => !existingNames.has(n));
                  return (
                    <>
                      {(availableRoots || []).map((node) => {
                        const isDisabled = disabledValueChainNames.has(node.name);
                        return (
                          <MenuItem
                            key={node.element_id}
                            value={String(node.element_id)}
                            disabled={isDisabled}
                            onClick={!isDisabled ? () => handleRootChange(String(node.element_id)) : undefined}
                            sx={isDisabled ? { color: theme.palette.text.disabled } : undefined}
                          >
                            {node.name}
                          </MenuItem>
                        );
                      })}
                      {placeholders.map((name) => (
                        <MenuItem key={`disabled-${name}`} disabled sx={{ color: theme.palette.text.disabled }}>
                          {name}
                        </MenuItem>
                      ))}
                    </>
                  );
                })()}
              </Select>
            </FormControl>
          </Box>




          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Typography variant="body2">
                 N Layers
              </Typography>
              <Tooltip
                placement="top-start"
                title={
                  <Typography variant="body2" sx={{ maxWidth: 420 }}>
                    The number of layers defines how many steps upstream in the value chain you want to view.
                  </Typography>
                }
              >
                <IconButton size="small" sx={{ p: 0.25 }}>
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Box>
              <input type="number" min="1" max="5" value={maxIterations} onChange={(e) => {
                const v = parseInt(e.target.value); if (v >= 1 && v <= 5) setMaxIterations(v);
              }} style={{ width: '60px', padding: '8px', border: `1px solid ${theme.palette.divider}`, borderRadius: '4px', fontSize: '14px' }} />
            </Box>
          </Box>

          {/* Year Selector (Postgres) */}
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Typography variant="body2">
                Min Value
              </Typography>
              <Tooltip
                placement="top-start"
                title={
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', maxWidth: 460 }}>
                    {'This filter removes very small flows from the chart.\n'}
                    {'Flows are first combined at HS code level, then the minimum value is applied.\n'}
                    {'In other words, it keeps meaningful grouped flows and hides minor ones.'}
                  </Typography>
                }
              >
                <IconButton size="small" sx={{ p: 0.25 }}>
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Box sx={{ position: 'relative', width: '84px' }}>
              <input type="number" min="0.1" max="10" step="0.1" value={(minFlowThreshold * 100).toFixed(1)} onChange={(e) => {
                const val = parseFloat(e.target.value); if (val >= 0.1 && val <= 10) setMinFlowThreshold(val / 100);
              }} style={{ width: '100%', padding: '8px 24px 8px 8px', border: `1px solid ${theme.palette.divider}`, borderRadius: '4px', fontSize: '14px' }} />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  lineHeight: 1,
                }}
              >
                %
              </Typography>
            </Box>
          </Box>
        </Box>
        <Divider sx={{ my: 2, opacity: 0.5 }} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          The number of layers defines how many steps upstream in the value chain you want to view.
          The minimum value threshold specifies the lowest percentage a flow must have in order to be represented.
        </Typography>
        </Paper>

        <Paper elevation={2} sx={{ p: 3, bgcolor: 'rgba(255,255,255,0.3)', border: '1px solid', borderColor: (t) => t.palette.divider, minWidth: 0, overflow: 'hidden' }}>

          <Typography variant="body1" gutterBottom>Select a Country to View its Trade at a given Year</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          By selecting a year we convert the trade data to the respective HS Code version.
          </Typography>
          <Divider sx={{ my: 2, opacity: 0.5 }} />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'minmax(0,1fr) minmax(0,1fr)' }, gap: 2, minWidth: 0 }}>
            {/* Country + year */}
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" gutterBottom>
                Country
              </Typography>
              <FormControl fullWidth size="small" sx={{ minWidth: 0 }}>
                <Select value={(accessibleCountries || []).some(c => String(c.code) === String(selectedCountry)) ? String(selectedCountry) : ''} onChange={(e) => setSelectedCountry(e.target.value)} displayEmpty MenuProps={{ disableScrollLock: true, PaperProps: { sx: { maxHeight: { xs: '56vh', md: 320 }, width: { xs: 'min(92vw, 420px)', md: 'auto' } } }, anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, MenuListProps: { dense: true } }}>
                  <MenuItem value=""><em>All Countries</em></MenuItem>
                  {accessibleCountries.map((c) => (
                    <MenuItem key={c.code} value={c.code}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" gutterBottom>
                Year
              </Typography>
              <FormControl fullWidth size="small" sx={{ minWidth: 0 }}>
                <Select value={(years || []).some(y => String(y.year) === String(selectedYear)) ? String(selectedYear) : ''} onChange={(e) => setSelectedYear(e.target.value)} displayEmpty MenuProps={{ disableScrollLock: true, PaperProps: { sx: { maxHeight: { xs: '56vh', md: 320 }, width: { xs: 'min(92vw, 420px)', md: 'auto' } } }, anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, MenuListProps: { dense: true } }}>
                  {years.map((y) => (
                    <MenuItem key={String(y.year)} value={String(y.year)}>{y.year}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
          <Divider sx={{ my: 2, opacity: 0.5 }} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Source: CEPII. BACI HS-22 Trade Database. Version 202501, CEPII, 2025,
            <Link href="https://www.cepii.fr/CEPII/en/bdd_modele/presentation.asp?id=37" target="_blank" rel="noopener noreferrer" sx={{ ml: 0.5 }}>
              https://www.cepii.fr/CEPII/en/bdd_modele/presentation.asp?id=37
            </Link>
          </Typography>
        </Paper>
      </Box>

      

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      



      {/* Color mode toggle (only when a country is selected, since RCA is country-specific) */}
      {selectedCountry && selectedYear && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 3, flexWrap: { xs: 'wrap', md: 'nowrap' }, bgcolor: 'rgba(255,255,255,0.3)', border: '1px solid', borderColor: (t) => t.palette.divider }}>
          <Typography variant="body2">Color by:</Typography>
          <ToggleButtonGroup size="small" value={colorMode} exclusive onChange={(e, v) => { if (v) setColorMode(v); }}>
            
            <ToggleButton value="exports">
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                <span>Exports</span>
                <InlineInfo
                  title={
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', maxWidth: 420 }}>
                      {'Exports to WORLD (USD) for each HS6 code.\n'}
                      {'Values come from the CEPII BACI trade data for the selected country and year.\n\n'}
                      {'Color scale: linear from 0 to the maximum exports across HS codes in the current Sankey.'}
                    </Typography>
                  }
                />
              </Box>
            </ToggleButton>
            <ToggleButton value="imports">
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                <span>Imports</span>
                <InlineInfo
                  title={
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', maxWidth: 420 }}>
                      {'Imports from WORLD (USD) for each HS6 code.\n'}
                      {'Values come from the CEPII BACI trade data for the selected country and year.\n\n'}
                      {'Color scale: linear from 0 to the maximum imports across HS codes in the current Sankey.'}
                    </Typography>
                  }
                />
              </Box>
            </ToggleButton>
            <ToggleButton value="balance">
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                <span>Trade balance</span>
                <InlineInfo
                  title={
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', maxWidth: 420 }}>
                      {'Trade balance is computed from exports and imports to WORLD for the selected country and year.\n\n'}
                      {'Normalized trade balance = (Exports − Imports) / (Exports + Imports).\n'}
                      {'Range: −1 (only imports) to +1 (only exports).'}
                    </Typography>
                  }
                />
              </Box>
            </ToggleButton>
            <ToggleButton value="rca">
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                <span>RCA</span>
                <InlineInfo
                  title={
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', maxWidth: 520 }}>
                      {'Revealed Comparative Advantage (RCA) is a trade indicator that measures whether a country is relatively specialized in exporting a particular product compared to the world average.\n\n'}
                      {'It compares the share of a product in a country’s exports to the product’s share in global trade, thereby “revealing” comparative advantage from observed export patterns rather than underlying costs.\n\n'}
                      {'RCA > 1 indicates that the country exports the product more intensively than the world average (a revealed comparative advantage), while RCA < 1 indicates a revealed disadvantage.\n\n'}
                      {'(For reference: RCA = (X_country,product / X_country,all) / (X_world,product / X_world,all).)'}
                    </Typography>
                  }
                />
              </Box>
            </ToggleButton>
          </ToggleButtonGroup>
          {colorMode === 'rca' && (
            <Tooltip title="Upper bound for RCA color saturation (log scale).">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">RCA cap</Typography>
                <input type="number" min="2" max="100" value={rcaUpper} onChange={(e)=> setRcaUpper(Math.max(2, Math.min(100, Number(e.target.value)||10)))} style={{ width: 70, padding: 6, border: `1px solid ${theme.palette.divider}`, borderRadius: 4 }} />
              </Box>
            </Tooltip>
          )}
          {/* Legend for node color scale (moved into this paper) */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: { xs: 0, md: 'auto' }, flexShrink: 0, flex: { xs: '1 1 100%', md: '0 0 auto' } }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', width: { xs: '100%', md: 'auto' } }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                {colorMode === 'rca'
                  ? `RCA (log-centered, cap ${rcaUpper})`
                  : (colorMode === 'exports'
                    ? 'Exports to WORLD (USD)'
                    : (colorMode === 'imports'
                      ? 'Imports from WORLD (USD)'
                      : 'Trade balance'))}
              </Typography>
              <Box
                sx={{
                  width: { xs: '100%', md: 200 },
                  height: 12,
                  background:
                    (colorMode === 'exports' || colorMode === 'imports')
                      ? 'linear-gradient(90deg, #fde725 0%, #5ec962 25%, #21918c 50%, #3b528b 75%, #440154 100%)'
                      : 'linear-gradient(90deg, #d32f2f 0%, #fbc02d 50%, #2e7d32 100%)',
                  borderRadius: 1
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: { xs: '100%', md: 200 }, mt: 0.5 }}>
                {colorMode === 'rca' ? (
                  <>
                    <Typography variant="caption" color="text.secondary">0</Typography>
                    <Typography variant="caption" color="text.secondary">1</Typography>
                    <Typography variant="caption" color="text.secondary">{`${rcaUpper}+`}</Typography>
                  </>
                ) : (colorMode === 'exports' || colorMode === 'imports') ? (
                  (() => {
                    const cap = colorMode === 'exports' ? tradeColorCaps.exportsCap : tradeColorCaps.importsCap;
                    const mid = cap > 0 ? (cap / 2) : 0;
                    return (
                      <>
                        <Typography variant="caption" color="text.secondary">$0</Typography>
                        <Typography variant="caption" color="text.secondary">{fmtCompactUsd.format(mid * 1000)}</Typography>
                        <Typography variant="caption" color="text.secondary">{fmtCompactUsd.format(cap * 1000)}</Typography>
                      </>
                    );
                  })()
                ) : (
                  <>
                    <Typography variant="caption" color="text.secondary">-1</Typography>
                    <Typography variant="caption" color="text.secondary">0</Typography>
                    <Typography variant="caption" color="text.secondary">1</Typography>
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Render Sankey only when graph data is ready and world summary (if country selected) is loaded. Otherwise show placeholder */}
      {(!selectedCountry || !selectedYear || !worldLoading) && graphData && graphData.nodes && graphData.nodes.length > 0 ? (
        <>
        <SankeyDiagram
          data={graphData}
          loading={loading || worldLoading}
          error={error}
          title={diagramTitle}
          height={600}
          minFlowThreshold={minFlowThreshold}
          requireRootConnectivity={false}
          enablePersistentFocus
          clearFocusTrigger={clearFocusTick}
          hsCodeBalanceMap={hsBalanceMap}
          hsCodeTradeMap={selectedCountry && selectedYear ? hsTradeMap : emptyTradeMap}
          rcaByCode={selectedCountry && selectedYear ? rcaByCode : emptyRcaMap}
          colorMode={selectedCountry && selectedYear ? colorMode : 'balance'}
          rcaUpper={rcaUpper}
          tradeColorCap={
            selectedCountry && selectedYear
              ? (colorMode === 'exports'
                ? tradeColorCaps.exportsCap
                : (colorMode === 'imports' ? tradeColorCaps.importsCap : undefined))
              : undefined
          }
          hsCodeDescriptionsByCode={hsDescriptionsByCode as any}
          codeLabelContext="hs"
          onNodeClick={handleNodeClick}
          legend={diagramLegend}
          valueChainInfo={valueChainInfo}
          valueChainShortcomings={valueChainShortcomings}
          key={`sankey-${selectedElementId}-${selectedCountry}-${selectedYear}`}
        />
        {/* Local Navigation Tabs directly under the Sankey */}
        <FeatureNavTabs />
        </>
      ) : (
        <Paper elevation={2} sx={{ p: 3, height: 600, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress size={28} />
        </Paper>
      )}

      {selectedGroup && (
        <Paper elevation={2} sx={{ p: 3, mt: 3, bgcolor: 'rgba(255,255,255,0.3)', border: '1px solid', borderColor: (t) => t.palette.divider }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Members of {selectedGroup.name}
              </Typography>
              {selectedGroup.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {selectedGroup.description}
                </Typography>
              )}
              <Chip label={`${selectedGroup.count} items`} size="small" />
            </Box>
            <Button variant="outlined" size="small" onClick={() => { setSelectedGroup(null); setSelectedMembers([]); setClearFocusTick(t => t + 1); }}>
              Clear selection
            </Button>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 2 }}>
            {selectedMembers.map((m, idx) => (
              <MemberCard key={`${m.element_id}-${idx}`} member={m} />
            ))}
          </Box>
        </Paper>
      )}

      {/* WORLD Trade Summary Table (only when a country is selected) */}
      {selectedCountry && selectedYear && (
      <>
        <Paper elevation={2} sx={{ p: 3, mt: 3, bgcolor: 'rgba(255,255,255,0.3)', border: '1px solid', borderColor: (t) => t.palette.divider }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Trade with WORLD (aggregated by HS code)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Country: {selectedCountryName} • Year: {selectedYear || '—'}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {(() => {
            const expMap = new Map();
            worldSummary.exports_to_world.forEach(r => expMap.set(r.hs_code, r));
            const impMap = new Map();
            worldSummary.imports_from_world.forEach(r => impMap.set(r.hs_code, r));
            const codes = Array.from(new Set([...expMap.keys(), ...impMap.keys()]));
            // Sort by path weight (outflow) desc
            codes.sort((a,b) => (hsPathWeightMap[b] || 0) - (hsPathWeightMap[a] || 0));
            const limited = codes.slice(0, visibleCount);
            return (
              <Box sx={{ width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <Table size="small" sx={{ minWidth: 760 }}>
                <TableHead>
                  <TableRow>
                    <TableCell width={48}></TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>HS Code</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        <span>Value share</span>
                        <Tooltip
                          arrow
                          placement="top"
                          title={
                            "Value share is computed from the Sankey weights: HS6 weight is the iteration-agnostic sum of Sankey link values attributed to that HS6 code. Value share = HS6 weight ÷ Σ(HS6 weights) across all HS6 codes present in the Sankey."
                          }
                        >
                          <IconButton
                            size="small"
                            onClick={(e) => e.stopPropagation()}
                            aria-label="Value share info"
                            sx={{ p: 0.25, color: 'text.secondary' }}
                          >
                            <InfoOutlinedIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Exports (USD thousands)</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Imports (USD thousands)</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Balance (USD thousands)</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Normalized</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>RCA</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {limited.length === 0 && (
                    <TableRow><TableCell colSpan={8}><Typography variant="body2" color="text.secondary">No data</Typography></TableCell></TableRow>
                  )}
                  {limited.map(code => {
                    const ev = Number((expMap.get(code)?.total_value) || 0);
                    const iv = Number((impMap.get(code)?.total_value) || 0);
                    const bal = ev - iv;
                    const norm = (ev + iv) > 0 ? bal / (ev + iv) : 0;
                    const code6 = normalizeHS(code);
                    const share = Number((hs6ValueShare as any)?.map?.[code6] || 0);
                    const open = !!expandedRows[code];
                    const rca = rcaByCode[normalizeHS(code)];
                    const chipBg =
                      colorMode === 'rca'
                        ? colorForNorm(rcaToNorm(rca))
                        : (colorMode === 'exports'
                          ? colorForTrade(ev, tradeColorCaps.exportsCap)
                          : (colorMode === 'imports'
                            ? colorForTrade(iv, tradeColorCaps.importsCap)
                            : colorForNorm(norm)));
                    return (
                      <React.Fragment key={code}>
                      <TableRow hover onClick={() => setExpandedRows(prev => ({ ...prev, [code]: !prev[code] }))} sx={{ cursor: 'pointer' }}>
                        <TableCell padding="checkbox">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setExpandedRows(prev => ({ ...prev, [code]: !prev[code] })); }} aria-label="expand row" aria-expanded={open}>
                            <ExpandMoreIcon sx={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 120ms ease' }} />
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Box component="span" sx={{ display: 'inline-block', width: 12, height: 12, borderRadius: '2px', mr: 1, bgcolor: chipBg, border: '1px solid rgba(0,0,0,0.2)' }} />
                          {code}
                        </TableCell>
                        <TableCell align="right">{fmtPct2.format(share)}</TableCell>
                        <TableCell align="right">{fmt2.format(ev)}</TableCell>
                        <TableCell align="right">{fmt2.format(iv)}</TableCell>
                        <TableCell align="right">{fmt2.format(bal)}</TableCell>
                        <TableCell align="right">{`${norm >= 0 ? '+' : ''}${fmt3.format(norm)}`}</TableCell>
                        <TableCell align="right">{rca !== null && rca !== undefined ? fmt3.format(rca) : '—'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                          <Collapse in={open} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 2 }}>
                              {hsDescriptionMap[code] && (
                                <Typography variant="body2" color="text.secondary" gutterBottom component="div">
                                  {(() => {
                                    const code6 = normalizeHS(code);
                                    const d = (hsDescriptionsByCode as any)?.[code6] || {};
                                    const code2 = code6 ? String(code6).slice(0, 2) : '';
                                    const code4 = code6 ? String(code6).slice(0, 4) : '';
                                    const d2 = d?.description_2 ? String(d.description_2) : '';
                                    const d4 = d?.description_4 ? String(d.description_4) : '';
                                    const sec = d?.section != null ? normalizeSectionCode(String(d.section).trim()) : '';
                                    const secDescRaw = d?.section_description != null ? String(d.section_description).trim() : '';
                                    const secDesc = secDescRaw ? softenAllCaps(secDescRaw) : '';
                                    const fallback6 = (hsDescriptionMap as any)[code6] ? String((hsDescriptionMap as any)[code6]) : '';
                                    const d6 = d?.description_6 != null ? String(d.description_6) : fallback6;

                                    const lines: Array<{ code: string; desc: string }> = [
                                      { code: code6, desc: d6 },
                                      { code: code4, desc: d4 },
                                      { code: code2, desc: d2 },
                                      { code: sec ? `Section ${sec}` : '', desc: secDesc },
                                    ].filter(it => it.code && it.desc);

                                    if (lines.length === 0) return null;
                                    return (
                                      <>
                                        {lines.map((it, idx) => (
                                          <React.Fragment key={`${it.code}-${idx}`}>
                                            {`${it.code}: ${it.desc}`}
                                            {idx < lines.length - 1 ? <br /> : null}
                                          </React.Fragment>
                                        ))}
                                      </>
                                    );
                                  })()}
                                </Typography>
                              )}
                              {/* Metrics removed per request; keep only HS description above */}
                              {Array.isArray(hsTechTypesMap[code]) && hsTechTypesMap[code].length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="subtitle2" gutterBottom>Product Variants under this HS code</Typography>
                                  {(() => {
                                    const items = hsTechTypesMap[code];
                                    const openTech = !!techExpanded[code];
                                    const toShow = openTech ? items : items.slice(0, 3);
                                    return (
                                      <>
                                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 1.5 }}>
                                          {toShow.map((t, idx) => {
                                            const key = `${code}-tt-${idx}`;
                                            const openCard = !!techCardExpanded[key];
                                            const text = t.funcText || '';
                                            const isLong = typeof text === 'string' && text.length > 140;
                                            return (
                                              <Paper key={key} elevation={1} sx={{ p: 1.5, bgcolor: 'rgba(255,255,255,0.3)', border: '1px solid', borderColor: (t) => t.palette.divider }}>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{t.name}</Typography>
                                                {text && (
                                                  <>
                                                    <Typography
                                                      variant="caption"
                                                      color="text.secondary"
                                                      sx={openCard ? { display: 'block', mt: 0.5 } : { display: '-webkit-box', mt: 0.5, WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                                                    >
                                                      {text}
                                                    </Typography>
                                                    {isLong && (
                                                      <IconButton size="small" onClick={() => setTechCardExpanded(prev => ({ ...prev, [key]: !openCard }))} aria-label={openCard ? 'Collapse' : 'Expand'} sx={{ mt: 0.5 }}>
                                                        {openCard ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}
                                                      </IconButton>
                                                    )}
                                                  </>
                                                )}
                                              </Paper>
                                            );
                                          })}
                                        </Box>
                                        {items.length > 3 && (
                                          <Box sx={{ mt: 1 }}>
                                            <IconButton size="small" onClick={() => setTechExpanded(prev => ({ ...prev, [code]: !openTech }))} aria-label={openTech ? 'Collapse' : 'Expand'}>
                                              <ExpandMoreIcon sx={{ transform: openTech ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 120ms ease' }} />
                                            </IconButton>
                                          </Box>
                                        )}
                                      </>
                                    );
                                  })()}
                                </Box>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
              </Box>
            );
          })()}
          {/* Infinite scroll: increase visible rows on scroll */}
          <Box sx={{ textAlign: 'center', mt: 1 }}>
            <Button size="small" onClick={() => setVisibleCount(c => c + 10)}>Load more</Button>
          </Box>
        </Paper>

        {/* Treemap (bottom of page) */}
        <Paper elevation={2} sx={{ p: 3, mt: 3, bgcolor: 'rgba(255,255,255,0.3)', border: '1px solid', borderColor: (t) => t.palette.divider }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) auto' }, gap: 2, alignItems: 'start' }}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Trade Treemap
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Country: {selectedCountryName} • Year: {selectedYear || '—'} • Values in USD thousands
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Each box is an HS code from the current value chain; box size shows {treemapFlow === 'exports' ? 'exports' : 'imports'} to WORLD. Choose HS6/4/2/Section for the level of aggregation, hover for details, or click a box to pin the breakdown below.
              </Typography>
            </Box>
            {/* Controls: compact row on desktop, 2-column grid + full-width export on mobile */}
            <Box
              sx={{
                display: { xs: 'grid', md: 'flex' },
                gridTemplateColumns: { xs: 'minmax(0,1fr) minmax(0,1fr)', md: 'unset' },
                gap: 1,
                alignItems: 'center',
                justifyContent: { md: 'flex-end' },
                justifySelf: { xs: 'stretch', md: 'end' },
                width: { xs: '100%', md: 'auto' },
              }}
            >
              <FormControl size="small" sx={{ minWidth: { xs: 0, md: 160 }, width: { xs: '100%', md: 160 } }}>
                <Select
                  value={treemapFlow}
                  onChange={(e) => setTreemapFlow(e.target.value as any)}
                  MenuProps={{ disableScrollLock: true }}
                >
                  <MenuItem value="exports">Exports</MenuItem>
                  <MenuItem value="imports">Imports</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: { xs: 0, md: 120 }, width: { xs: '100%', md: 120 } }}>
                <Select
                  value={String(treemapLevel)}
                  onChange={(e) => {
                    const v = String(e.target.value);
                    if (v === 'section') setTreemapLevel('section');
                    else setTreemapLevel(Number(v) as any);
                  }}
                  MenuProps={{ disableScrollLock: true }}
                >
                  <MenuItem value="6">HS6</MenuItem>
                  <MenuItem value="4">HS4</MenuItem>
                  <MenuItem value="2">HS2</MenuItem>
                  <MenuItem value="section">Section</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                size="small"
                startIcon={<FileDownloadIcon />}
                onClick={exportTreemapXlsx}
                disabled={treemapExporting || !selectedCountry || !selectedYear || !Array.isArray(sankeyHSCodes) || sankeyHSCodes.length === 0}
                sx={{
                  gridColumn: { xs: '1 / -1', md: 'auto' },
                  justifySelf: { xs: 'stretch', md: 'auto' },
                  whiteSpace: 'nowrap',
                }}
              >
                {treemapExporting ? 'Exporting…' : 'Export XLSX'}
              </Button>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />
          
          {treemapExportError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {treemapExportError}
            </Alert>
          )}

          {/* Explanation + legend: two-column on desktop, stacked on mobile */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 300px' },
              gap: 2,
              alignItems: 'start',
              mb: 1.5,
            }}
          >
            <Paper
              elevation={1}
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'success.main',
                bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(16,185,129,0.12)' : 'rgba(76, 175, 80, 0.1)'),
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <InfoOutlinedIcon sx={{ mt: '2px', color: 'success.main' }} fontSize="small" />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={
                      treemapInfoExpanded
                        ? undefined
                        : {
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: { xs: 2, md: 2 },
                            overflow: 'hidden',
                          }
                    }
                  >
                    Colors show <b>relative value share</b> derived from Sankey flow weights.
                  We compute: (1) <b>HS6 weight</b> = sum of Sankey link values attributed to each HS6 code (ignoring iteration),
                  (2) <b>segment weight</b> = sum of included HS6 weights (for HS6 segments it’s just that HS6; for HS4/HS2/Section it’s the sum of all HS6 codes inside the segment),
                  (3) <b>relative value share</b> = segment weight ÷ Σ(segment weights) across <b>all HS codes present in the Sankey</b> at the current HS level (even if some segments have no trade data),
                  (4) <b>color</b> = log10(share + 1e-6), clipped to the 5th–95th percentile and mapped using a reversed Viridis color scale (yellow = low, purple = high).
                  Grey means share is 0/unknown. Rank is based on segment weight (1 = highest) for the current view.
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setTreemapInfoExpanded((v) => !v)}
                  sx={{ alignSelf: 'center', px: 0.5, minWidth: 0, color: 'success.main', textAlign: 'center' }}
                  aria-expanded={treemapInfoExpanded}
                >
                  {treemapInfoExpanded ? 'Show less' : 'Read more'}
                </Button>
              </Box>
            </Paper>

            <Box
              sx={{
                width: '100%',
                border: '1px solid',
                borderColor: (t) => t.palette.divider,
                borderRadius: 1.5,
                p: 1,
                bgcolor: 'rgba(255,255,255,0.35)',
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 700 }}>
                Color = relative value share
              </Typography>
              <Box sx={{ mt: 0.5, height: 10, width: '100%', borderRadius: 1, background: 'linear-gradient(90deg, #fde725 0%, #5ec962 25%, #21918c 50%, #3b528b 75%, #440154 100%)' }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">low</Typography>
                <Typography variant="caption" color="text.secondary">high</Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                {`(P5–P95, log-scaled)`}
              </Typography>
            </Box>
          </Box>

          {worldLoading ? (
            <Box sx={{ height: 420, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          ) : treemapData.length === 0 ? (
            <Alert severity="info">
              No {treemapFlow} data available for the currently displayed HS codes.
            </Alert>
          ) : (
            <Box sx={{ height: 480 }}>
              <ReactECharts
                option={treemapOption}
                style={{ height: '100%', width: '100%' }}
                opts={{ renderer: 'canvas' }}
                notMerge
                onEvents={treemapEvents}
              />
            </Box>
          )}
        </Paper>

        {treemapSelection && (
          <Paper elevation={2} sx={{ p: 3, mt: 2, bgcolor: 'rgba(255,255,255,0.3)', border: '1px solid', borderColor: (t) => t.palette.divider }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {(() => {
                    const levelLabel =
                      treemapSelection.level === 'section'
                        ? 'Section'
                        : treemapSelection.level === 6
                          ? 'HS6'
                          : treemapSelection.level === 4
                            ? 'HS4'
                            : 'HS2';
                    if (treemapSelection.level !== 'section') {
                      return `Selected: ${levelLabel} ${treemapSelection.code}`;
                    }
                    const secCode = normalizeSectionCode(String(treemapSelection.code || ''));
                    const firstHs6 = treemapSelection?.hs6Items?.[0]?.code ? normalizeHS(treemapSelection.hs6Items[0].code) : '';
                    const d = firstHs6 ? (hsDescriptionsByCode as any)?.[firstHs6] || {} : {};
                    const raw = d?.section_description != null ? String(d.section_description) : '';
                    const desc = raw ? truncateText(softenAllCaps(raw), 160) : '';
                    return `Selected: ${levelLabel} ${secCode}${desc ? ` — ${desc}` : ''}`;
                  })()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Exports: {fmt2.format(Number(treemapSelection.exportsTotal) || 0)} USD thousands • Imports: {fmt2.format(Number(treemapSelection.importsTotal) || 0)} USD thousands
                </Typography>
              </Box>
              <Button size="small" variant="outlined" onClick={() => setTreemapSelection(null)}>
                Clear
              </Button>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <Table size="small" sx={{ minWidth: 760 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>HS6 Code</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Exports (USD thousands)</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Imports (USD thousands)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(!treemapSelection.hs6Items || treemapSelection.hs6Items.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography variant="body2" color="text.secondary">No HS6 items available.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {(treemapSelection.hs6Items || [])
                    .slice()
                    .sort((a, b) => {
                      const ak = treemapFlow === 'exports' ? (Number((a as any).exportsValue) || 0) : (Number((a as any).importsValue) || 0);
                      const bk = treemapFlow === 'exports' ? (Number((b as any).exportsValue) || 0) : (Number((b as any).importsValue) || 0);
                      return bk - ak;
                    })
                    .map((it) => (
                      <TableRow key={it.code} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{it.code}</TableCell>
                        <TableCell sx={{ minWidth: 320 }}>
                          {(() => {
                            const code6 = normalizeHS(it.code);
                            const d = (hsDescriptionsByCode as any)?.[code6] || {};
                            const code4 = code6 ? String(code6).slice(0, 4) : '';
                            const code2 = code6 ? String(code6).slice(0, 2) : '';
                            const d6 = d?.description_6 != null ? String(d.description_6) : (it.description ? String(it.description) : '');
                            const d4 = d?.description_4 ? String(d.description_4) : '';
                            const d2 = d?.description_2 ? String(d.description_2) : '';
                            const sec = d?.section != null ? normalizeSectionCode(String(d.section).trim()) : '';
                            const secDescRaw = d?.section_description != null ? String(d.section_description).trim() : '';
                            const secDesc = secDescRaw ? softenAllCaps(secDescRaw) : '';
                            const lines: Array<{ code: string; desc: string }> = [
                              { code: code6, desc: d6 },
                              { code: code4, desc: d4 },
                              { code: code2, desc: d2 },
                              { code: sec ? `Section ${sec}` : '', desc: secDesc },
                            ].filter(x => x.code && x.desc);
                            if (lines.length === 0) return '—';
                            return (
                              <Box component="div" sx={{ whiteSpace: 'normal' }}>
                                {lines.map((ln, idx) => (
                                  <React.Fragment key={`${it.code}-desc-${ln.code}-${idx}`}>
                                    {`${ln.code}: ${ln.desc}`}
                                    {idx < lines.length - 1 ? <br /> : null}
                                  </React.Fragment>
                                ))}
                              </Box>
                            );
                          })()}
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{fmt2.format(Number((it as any).exportsValue) || 0)}</TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{fmt2.format(Number((it as any).importsValue) || 0)}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </Box>
          </Paper>
        )}
      </>
      )}
    </Box>
  );
};

const MemberCard = ({ member }) => {
  const p = member?.properties || {};
  const title = p.name || (Array.isArray(p.hs_code) ? p.hs_code[0] : member.id);
  const funcText = p.function || '';
  const hsDesc = Array.isArray(p.hs_code_description) ? p.hs_code_description[0] : p.hs_code_description;
  const [expanded, setExpanded] = useState(false);

  const text = funcText || hsDesc || '';
  const isLong = typeof text === 'string' && text.length > 140;

  return (
    <Paper elevation={1} sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.3)', border: '1px solid', borderColor: (t) => t.palette.divider }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }} gutterBottom>
        {title}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
        {member.labels?.[0] || 'TechnologyType'} • Iteration: {p.n_iteration ?? '—'}
      </Typography>
      {text && (
        <>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={expanded ? {} : {
              display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden'
            }}
          >
            {text}
          </Typography>
          {isLong && (
            <IconButton size="small" onClick={() => setExpanded(v => !v)} aria-label={expanded ? 'Collapse' : 'Expand'} sx={{ mt: 0.5 }}>
              {expanded ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}
            </IconButton>
          )}
        </>
      )}
    </Paper>
  );
};

export default HSGroupedHSCodeAnalysis;


